import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { Link, useSearchParams, useNavigate, useLocation } from "react-router-dom";
import { connect } from "react-redux";
import styled, { createGlobalStyle, keyframes, css } from "styled-components";
import { format, formatDistanceToNow } from "date-fns";
import { parseDate } from "../utils/helpers/date";
import {
  User,
  MessageCircle,
  DollarSign,
  Calendar,
  Briefcase,
  MapPin,
  Star,
  PlusCircle,
  Image as ImageIcon,
  Clock,
  Search,
  Navigation,
  Target,
  ArrowUpDown,
  Settings,
  Filter,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  X,
  Menu,
  SlidersHorizontal,
  Bell,
  LogIn,
  ExternalLink,
  Flag,
  Pencil,
  Check,
  Link2,
  MoreHorizontal,
} from "lucide-react";

import { request } from "../services/networking/request";
import { API_CONSTANT_MAP } from "../utils/constants/endpoints";
import Loader from "../components/Loading";
import NavBar from "../components/layout/NavBar";
import BackButton from "../components/BackButton";
import SafetyTip from "../components/safety/SafetyTip";
import VerificationBadge from "../components/common/VerificationBadge";
import UserRating from "../components/common/UserRating";
import ImageModal from "../components/ImageModal";
import Avatar from "../components/Avatar";

// Utility function to normalize URL (add https:// if missing)
const normalizeURL = (string) => {
  if (!string) return null;
  let url = string.trim();
  if (!url) return null;
  // Add https:// if no protocol
  if (!/^https?:\/\//i.test(url)) {
    url = 'https://' + url;
  }
  return url;
};

// Utility function to validate if a string is a URL
const isValidURL = (string) => {
  try {
    const normalized = normalizeURL(string);
    if (!normalized) return false;
    const url = new URL(normalized);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch (_) {
    return false;
  }
};

// Utility function to get domain name from URL
const getDomainName = (url) => {
  try {
    const normalized = normalizeURL(url);
    if (!normalized) return url;
    const domain = new URL(normalized).hostname;
    return domain.replace('www.', '');
  } catch (_) {
    return url?.trim() || url;
  }
};

// Utility function to linkify URLs in text
const linkifyText = (text) => {
  if (!text) return null;
  const urlRegex = /(https?:\/\/[^\s]+)|(www\.[^\s]+)/g;
  const parts = text.split(urlRegex);

  return parts.map((part, index) => {
    if (!part) return null;
    if (part.match(/^https?:\/\//) || part.match(/^www\./)) {
      const href = part.startsWith('www.') ? `https://${part}` : part;
      return (
        <a
          key={index}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            color: 'hsl(217, 91%, 60%)',
            textDecoration: 'none',
            wordBreak: 'break-all'
          }}
          onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
          onMouseOut={(e) => e.target.style.textDecoration = 'none'}
          onClick={(e) => e.stopPropagation()}
        >
          {part}
        </a>
      );
    }
    return part;
  });
};

/* =============== CATEGORIES CONSTANT =============== */
// IDs must match database categories table
const CATEGORIES = [
  { id: 22, name: "Administrative Support" },
  { id: 12, name: "Appliance Installation" },
  { id: 26, name: "Caregiving Services" },
  { id: 24, name: "Carpentry" },
  { id: 16, name: "Computer & Tech Help" },
  { id: 31, name: "Cooking / Meal Prep" },
  { id: 15, name: "Delivery & Pickup" },
  { id: 21, name: "Design & Creative" },
  { id: 33, name: "Drywall" },
  { id: 10, name: "Electrical Work" },
  { id: 18, name: "Event Services" },
  { id: 37, name: "Fencing" },
  { id: 36, name: "Flooring" },
  { id: 2, name: "Furniture Assembly" },
  { id: 11, name: "Gardening" },
  { id: 1, name: "General Mounting" },
  { id: 8, name: "Handyman Services" },
  { id: 25, name: "Hauling" },
  { id: 13, name: "Home Organization" },
  { id: 3, name: "House Cleaning" },
  { id: 30, name: "HVAC Services" },
  { id: 35, name: "Junk Removal" },
  { id: 7, name: "Landscaping" },
  { id: 5, name: "Moving Help" },
  { id: 23, name: "Other" },
  { id: 9, name: "Painting" },
  { id: 14, name: "Pet Care" },
  { id: 19, name: "Photography" },
  { id: 6, name: "Plumbing" },
  { id: 38, name: "Pool Services" },
  { id: 34, name: "Pressure Washing" },
  { id: 32, name: "Rides & Transport" },
  { id: 29, name: "Roof Services" },
  { id: 27, name: "Sewing or Clothing Repairs" },
  { id: 17, name: "Tutoring & Lessons" },
  { id: 20, name: "Writing & Editing" },
  { id: 4, name: "Yard Work" }
];

const USE_CATEGORIES_CONSTANT = true; // Set to false to use API call

/* =============== DESIGN TOKENS =============== */
const theme = {
  colors: {
    primary: "hsl(217, 91%, 60%)",
    primaryHover: "hsl(217, 91%, 50%)",
    primaryLight: "hsl(217, 91%, 96%)",
    primaryText: "hsl(217, 91%, 50%)",
    secondary: "#6366f1",
    textHeading: "#0f172a",
    textPrimary: "#0f172a",
    textSecondary: "#334155",
    textBody: "#334155",
    textMuted: "#64748b",
    surface: "#ffffff",
    surfaceHover: "#f8fafc",
    background: "#f8fafc",
    border: "#e2e8f0",
    borderLight: "#f1f5f9",
    accent: "#10b981",
    warning: "#f59e0b",
    error: "#ef4444",
  },
  gradients: {
    primary: "none",
    secondary: "none",
    surface: "none",
  },
  shadows: {
    sm: "0 1px 2px rgba(15, 23, 42, 0.04)",
    md: "0 4px 12px rgba(15, 23, 42, 0.06)",
    lg: "0 6px 16px rgba(15, 23, 42, 0.06)",
    navbar: "0 1px 2px rgba(15, 23, 42, 0.04)",
  },
  spacing: {
    xs: "0.25rem",
    sm: "0.5rem",
    md: "0.75rem",
    lg: "1rem",
    xl: "1.5rem",
    xxl: "2rem",
  },
  borderRadius: {
    sm: "0.375rem", 
    md: "0.875rem",
    lg: "0.875rem",
    xl: "0.875rem",
    full: "9999px",
  },
  typography: {
    fontFamily:
      "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    fontWeight: {
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
      extrabold: 800,
    },
  },
};

/* =============== GLOBAL/ANIM =============== */
const fadeIn = keyframes`from{opacity:0}to{opacity:1}`;
const slideInUp = keyframes`from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}`;
const GlobalStyle = createGlobalStyle`
  body {
    background: ${theme.colors.background};
    font-family: ${theme.typography.fontFamily};
    color: ${theme.colors.textPrimary};
    font-weight: ${theme.typography.fontWeight.normal};
  }

  /* Show back button in Capacitor app on mobile */
  body.capacitor .capacitor-back-button {
    @media (max-width: 768px) {
      display: flex !important;
    }
  }
`;

/* =============== LAYOUT/COMMON =============== */
const Page = styled.div`
  min-height: 100vh;
  padding: 64px 0px;
  background: ${theme.colors.background};
  font-family: ${theme.typography.fontFamily};
`;

const Container = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 20px;

  @media (max-width: 1024px) {
    padding: 0;
    width: 100%;
    max-width: 100vw;
    overflow-x: hidden;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 360px 1fr;
  gap: 24px;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 0;
  }
`;

/* ========================================= */
/* ===== REDESIGNED FILTER SECTION START ===== */
/* ========================================= */

const SearchBarContainer = styled.div`
  margin-bottom: ${theme.spacing.lg};
  display: flex;
  align-items: center;
  gap: ${theme.spacing.lg};
  flex-wrap: wrap;
  position: relative;
  z-index: 1050;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: ${theme.spacing.md};
    margin-bottom: ${theme.spacing.md};
  }
`;

const SearchBar = styled.div`
  position: relative;
  flex: 1;
  min-width: 320px;
  background: ${theme.colors.surface};
  border-radius: 1rem;
  border: 1px solid ${theme.colors.border};
  box-shadow: none;
  transition: border-color 0.2s ease;

  &:hover {
    border-color: ${theme.colors.textMuted};
  }

  &:focus-within {
    border-color: ${theme.colors.primary};
    box-shadow: 0 0 0 2px ${theme.colors.primaryLight};
  }

  @media (max-width: 768px) {
    min-width: auto;
    width: 100%;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.65rem 3.25rem 0.65rem 2.75rem;
  border: none;
  border-radius: 2rem;
  font-size: 0.85rem;
  font-weight: ${theme.typography.fontWeight.medium};
  font-family: ${theme.typography.fontFamily};
  background: transparent;
  color: ${theme.colors.textPrimary};
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);

  &:focus {
    outline: none;
  }

  &::placeholder {
    color: ${theme.colors.textMuted};
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: ${theme.colors.textMuted};
  pointer-events: none;
  transition: color 0.2s ease;

  ${SearchBar}:focus-within & {
    color: ${theme.colors.primary};
  }
`;

const SearchButton = styled.button`
  position: absolute;
  right: 0.25rem;
  top: 50%;
  transform: translateY(-50%);
  background: ${theme.colors.primary};
  color: white !important;
  border: none;
  border-radius: 0.75rem;
  width: 2.25rem;
  height: 2.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s ease;
  font-weight: ${theme.typography.fontWeight.semibold};
  box-shadow: none;

  &:hover {
    background: ${theme.colors.primaryHover};
    color: white !important;
  }

  &:active {
    color: white !important;
  }
`;

const FilterPillsRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  flex-wrap: wrap;
  padding-top: ${theme.spacing.xs};

  @media (max-width: 768px) {
    width: 100%;
    justify-content: flex-start;
    gap: ${theme.spacing.xs};
  }
`;

const FilterPill = styled.button`
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  padding: ${theme.spacing.sm} ${theme.spacing.lg};
  border-radius: 999px;
  font-size: 0.8125rem;
  font-weight: ${theme.typography.fontWeight.semibold};
  font-family: ${theme.typography.fontFamily};
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease, color 0.2s ease;
  border: 1px solid ${theme.colors.border};
  background: ${theme.colors.surface};
  box-shadow: none;
  color: ${theme.colors.textSecondary};

  &:hover {
    border-color: ${theme.colors.textMuted};
    background: ${theme.colors.surfaceHover};
    color: ${theme.colors.textPrimary};
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px ${theme.colors.primaryLight};
  }

  &:active {
    transform: translateY(0px);
  }

  ${(props) =>
    props.filled
      ? `
    background: ${theme.colors.primaryLight};
    border-color: ${theme.colors.primaryLight};
    color: ${theme.colors.primary} !important;
    font-weight: ${theme.typography.fontWeight.bold};
    box-shadow: none;

    &:hover {
      background: ${theme.colors.primaryLight};
      color: ${theme.colors.primary} !important;
    }

    &:active {
      color: white !important;
    }
  `
      : props.danger
      ? `
    background: #fff7f7;
    border-color: #fecaca;
    color: #dc2626 !important;
    font-weight: ${theme.typography.fontWeight.semibold};

    &:hover {
      background: #fff1f1;
      border-color: #fca5a5;
      color: #b91c1c !important;
    }

    &:active {
      transform: translateY(0px);
      color: hsl(0, 84%, 40%) !important;
    }
  `
      : `
    color: ${theme.colors.textSecondary};

    &:hover {
      color: ${theme.colors.textPrimary};
    }
  `}
`;

const ResultsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 4px;
`;

const ResultsCount = styled.div`
  font-size: 14px;
  color: #5e6670;
  font-weight: 400;
`;

const ClearButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border-radius: 999px;
  background: #fff7f7;
  color: #dc2626;
  font-size: 0.8125rem;
  font-weight: ${theme.typography.fontWeight.semibold};
  font-family: ${theme.typography.fontFamily};
  border: 1px solid #fecaca;
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease, color 0.2s ease;
  box-shadow: none;

  &:hover {
    background: #fff1f1;
    border-color: #fca5a5;
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(220, 38, 38, 0.15), ${theme.shadows.sm};
  }

  &:active {
    transform: translateY(0px);
  }
`;

// UPDATED: Location Dropdown with fixed positioning to prevent clipping
const DropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.lg};
  z-index: 10000;
  margin-top: ${theme.spacing.sm};
  min-width: 280px;
  transform-origin: top;
  animation: ${slideInUp} 0.2s ease;
`;

const DropdownContent = styled.div`
  padding: ${theme.spacing.xl};
`;

const DropdownLabel = styled.label`
  display: block;
  font-size: 0.875rem;
  font-weight: ${theme.typography.fontWeight.medium};
  color: ${theme.colors.textSecondary};
  margin-bottom: ${theme.spacing.md};
`;

const DropdownInput = styled.input`
  width: 100%;
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  font-size: 0.875rem;
  background: ${theme.colors.surface};
  color: ${theme.colors.textPrimary};
  font-family: ${theme.typography.fontFamily};
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
    background: ${theme.colors.surface};
    box-shadow: 0 0 0 3px ${theme.colors.primaryLight};
  }

  &::placeholder {
    color: ${theme.colors.textMuted};
  }

  &:hover {
    border-color: ${theme.colors.borderHover};
  }
`;

const BudgetInputRow = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
  align-items: center;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  min-width: 0;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: ${theme.spacing.sm};
    align-items: stretch;

    span {
      align-self: flex-start;
      font-size: 0.75rem;
      color: ${theme.colors.textMuted};
      margin: 0;
    }
  }

  @media (max-width: 480px) {
    gap: ${theme.spacing.xs};
  }

  span {
    color: ${theme.colors.textSecondary};
    font-size: 0.875rem;
    font-weight: ${theme.typography.fontWeight.medium};
    text-align: center;
    flex-shrink: 0;
    min-width: 20px;
  }
`;

const DropdownActions = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
  justify-content: flex-end;
  margin-top: ${theme.spacing.xl};
  padding-top: ${theme.spacing.lg};
  border-top: 1px solid ${theme.colors.border};
`;

const DropdownButton = styled.button`
  padding: ${theme.spacing.sm} ${theme.spacing.lg};
  border-radius: ${theme.borderRadius.full};
  font-size: 0.875rem;
  font-weight: ${theme.typography.fontWeight.semibold};
  font-family: ${theme.typography.fontFamily};
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  border: none;

  ${(props) =>
    props.primary
      ? `
    background: ${theme.gradients.primary};
    color: white !important;
    box-shadow: ${theme.shadows.sm};

    &:hover {
      background: ${theme.colors.primaryHover};
      transform: translateY(-1px);
      box-shadow: ${theme.shadows.md};
      color: white !important;
    }

    &:active {
      transform: translateY(0);
      color: white !important;
    }
  `
      : `
    background: ${theme.colors.surfaceHover};
    color: ${theme.colors.textSecondary};
    border: 1px solid ${theme.colors.border};

    &:hover {
      background: ${theme.colors.border};
      color: ${theme.colors.textPrimary};
    }
  `}
`;

const DropdownOption = styled.div`
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  cursor: pointer;
  font-size: 0.875rem;
  color: ${theme.colors.textSecondary};
  border-radius: ${theme.borderRadius.md};
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    background: ${theme.colors.surfaceHover};
    color: ${theme.colors.textPrimary};
  }

  &:active {
    background: ${theme.colors.border};
  }
`;

const MobileFilterButton = styled.button`
  display: none;

  @media (max-width: 1024px) {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    width: calc(100% - 32px);
    max-width: calc(100vw - 32px);
    padding: 14px 18px;
    margin: 0 16px 16px;
    background: white;
    border: 1px solid #e8ecf4;
    border-radius: 12px;
    color: ${theme.colors.textBody};
    font-size: 16px;
    font-weight: 600;
    box-shadow: 0 2px 8px rgba(15, 23, 42, 0.04);
    transition: all 0.2s ease;
    cursor: pointer;
    line-height: 1.2;
    min-height: 52px;
    box-sizing: border-box;

    &:hover {
      background: #f8fafc;
      border-color: ${theme.colors.primary};
      box-shadow: 0 4px 12px rgba(15, 23, 42, 0.08);
      transform: translateY(-1px);
    }

    &:active {
      transform: translateY(0px);
      box-shadow: 0 2px 6px rgba(15, 23, 42, 0.06);
    }

    .filter-content {
      display: flex;
      align-items: center;
      gap: 10px;
    }

    .filter-badge {
      background: ${theme.colors.primaryLight};
      color: ${theme.colors.primary};
      font-size: 12px;
      font-weight: 700;
      padding: 4px 8px;
      border-radius: 8px;
      min-width: 20px;
      height: 20px;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    svg {
      flex-shrink: 0;
      display: block;
      color: ${theme.colors.primary};
    }

    span:first-of-type {
      font-weight: 600;
      font-size: 16px;
      line-height: 1.2;
      color: inherit;
    }
  }
`;

const MobileHeader = styled.div`
  display: none;

  @media (max-width: 1024px) {
    display: flex;
    align-items: center;
    gap: 8px;
    width: calc(100% - 24px);
    max-width: calc(100vw - 24px);
    margin: 8px 12px 12px;
    box-sizing: border-box;
    padding-right: 8px; /* Extra space for filter badge */
  }
`;

const MobileSearchContainer = styled.div`
  flex: 1;
  max-width: calc(100% - 60px); /* Leave space for filter button */
  position: relative;
  background: ${theme.colors.surface};
  border-radius: 2rem;
  border: 1px solid ${theme.colors.border};
  box-shadow: ${theme.shadows.sm};
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    border-color: ${theme.colors.textMuted};
    box-shadow: ${theme.shadows.md};
  }

  &:focus-within {
    border-color: ${theme.colors.primary};
    box-shadow: 0 0 0 3px ${theme.colors.primaryLight};
    transform: translateY(-1px);
  }
`;

const MobileSearchInput = styled.input`
  width: 100%;
  padding: 0.75rem 3.5rem 0.75rem 1rem;
  border: none;
  border-radius: 2rem;
  font-size: 0.875rem;
  font-weight: ${theme.typography.fontWeight.medium};
  font-family: ${theme.typography.fontFamily};
  background: transparent;
  color: ${theme.colors.textPrimary};
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  box-sizing: border-box;

  &:focus {
    outline: none;
  }

  &::placeholder {
    color: ${theme.colors.textMuted};
  }
`;

const MobileSearchIconLeft = styled.div`
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: ${theme.colors.textMuted};
  pointer-events: none;
  transition: color 0.2s ease;

  ${MobileSearchContainer}:focus-within & {
    color: ${theme.colors.primary};
  }
`;

const MobileSearchButton = styled.button`
  position: absolute;
  right: 0.25rem;
  top: 50%;
  transform: translateY(-50%);
  background: ${theme.colors.primary};
  color: white !important;
  border: 1px solid ${theme.colors.primary};
  border-radius: 1.5rem;
  width: 2.5rem;
  height: 2.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  font-weight: ${theme.typography.fontWeight.semibold};
  box-shadow: 0 2px 4px rgba(59, 130, 246, 0.2);
  z-index: 10; /* Ensure button stays above other elements */

  &:hover {
    background: ${theme.colors.primaryHover};
    transform: translateY(-50%) translateY(-2px);
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
    color: white !important;
  }

  &:active {
    transform: translateY(-50%) translateY(-1px);
    box-shadow: 0 2px 8px rgba(59, 130, 246, 0.25);
  }
`;

const MobileFilterIcon = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  background: white;
  border: 2px solid #e5e7eb;
  border-radius: 10px;
  color: #374151;
  transition: all 0.2s ease;
  cursor: pointer;
  position: relative;
  margin-left: 8px;
  overflow: visible; /* Allow badge to extend outside */

  &:hover {
    background: #f9fafb;
    border-color: #3b82f6;
    color: #3b82f6;
  }

  &:focus {
    outline: none;
    border-color: #3b82f6;
    color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  .filter-badge {
    position: absolute;
    top: -2px;
    right: -2px;
    background: ${theme.colors.primary};
    color: white;
    font-size: 10px;
    font-weight: 700;
    padding: 2px 5px;
    border-radius: 8px;
    min-width: 16px;
    height: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
    z-index: 10;
  }

  svg {
    flex-shrink: 0;
    color: ${theme.colors.textPrimary};
  }
`;

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.4);
  z-index: 9998;
`;
const MobileSheet = styled.div`
  position: fixed;
  inset: 0;
  background: #ffffff;
  z-index: 400010;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100vh;
  max-width: 100vw;
  min-width: 0;
  overflow: hidden;
  box-sizing: border-box;
`;

const MobileSheetHeader = styled.div`
  padding: 18px 16px 12px;
  border-bottom: 1px solid ${theme.colors.border};
  flex-shrink: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #ffffff;
  position: relative;

  h2 {
    margin: 0;
    font-size: 1.1rem;
    font-weight: 600;
    color: ${theme.colors.textPrimary};
    display: flex;
    align-items: center;
    gap: 12px;

    svg {
      color: ${theme.colors.primary};
    }
  }

  .close-button {
    background: #ffffff;
    border: none;
    border-radius: 8px;
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s ease;
    color: ${theme.colors.textMuted};

    &:hover {
      background: #f1f5f9;
      color: ${theme.colors.textBody};
    }

    &:active {
      transform: none;
    }
  }
`;

const MobileSheetContent = styled.div`
  padding: 16px 16px 200px 16px;
  overflow-y: auto;
  flex: 1;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  min-width: 0;

  @media (max-width: 375px) {
    padding: 12px 12px 200px 12px;
  }
`;

const MobileSheetFooter = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 16px;
  padding-bottom: calc(16px + env(safe-area-inset-bottom));
  border-top: 1px solid ${theme.colors.border};
  background: #ffffff;
  display: flex;
  gap: 16px;
  justify-content: space-between;
  z-index: 400020;
  backdrop-filter: none;
  box-shadow: none;
`;

const MobileFilterForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  min-width: 0;

  @media (max-width: 375px) {
    gap: 14px;
  }
`;

const MobileFilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  min-width: 0;

  &:last-child {
    margin-bottom: 0;
  }

  ${props => props.hasValue && `
    .filter-label {
      color: #1d4ed8;
      font-weight: 700;
    }
  `}
`;

const MobileFilterLabel = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 4px;
  transition: all 0.2s ease;
`;

const MobileFilterInput = styled.input`
  width: 100%;
  max-width: 100%;
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 10px;
  font-size: 14px;
  background: white;
  color: #374151;
  transition: all 0.2s ease;
  box-sizing: border-box;
  min-width: 0;
  flex: 1;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
  }

  &:hover {
    border-color: #9ca3af;
  }

  &::placeholder {
    color: #9ca3af;
    font-size: 13px;
  }

  ${props => props.hasValue && `
    border-color: #1d4ed8;
    font-weight: 500;
  `}

  @media (max-width: 375px) {
    padding: 8px 10px;
    font-size: 13px;

    &::placeholder {
      font-size: 12px;
    }
  }
`;

const MobileFilterSelect = styled.select`
  width: 100%;
  max-width: 100%;
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 10px;
  font-size: 14px;
  background: white;
  color: #374151;
  transition: all 0.2s ease;
  box-sizing: border-box;
  min-width: 0;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
  }

  &:hover {
    border-color: #9ca3af;
  }

  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23475569' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6,9 12,15 18,9'%3e%3c/polyline%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 10px center;
  background-size: 14px;
  appearance: none;

  ${props => props.hasValue && `
    border-color: #1d4ed8;
    font-weight: 500;
  `}

  @media (max-width: 375px) {
    padding: 8px 10px;
    font-size: 13px;
    background-position: right 8px center;
    background-size: 12px;
  }
`;

const MobileButton = styled.button`
  flex: 1;
  padding: 14px 16px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  min-height: 48px;
  position: relative;
  overflow: hidden;

  ${(props) =>
    props.primary
      ? `
    background: ${theme.colors.primary};
    color: white;
    box-shadow: none;

    &:hover {
      background: ${theme.colors.primaryHover};
    }

    &:active {
      transform: none;
    }
  `
      : `
    background: #f1f5f9;
    color: #64748b;
    border: 1px solid #e2e8f0;

    &:hover {
      background: #e2e8f0;
      color: #475569;
      border-color: #cbd5e1;
    }

    &:active {
      background: #cbd5e1;
      transform: scale(0.98);
    }
  `}
`;

/* ========================================= */
/* ====== OLD FILTER COMPONENTS (removed) ====== */
/* ========================================= */

// Old components are removed from the code for clarity and to prevent duplication.
// `FilterSummaryBar`, `Chip`, `FilterToggle`, `HeaderCard`, `FilterForm`, `MainRow`, etc.
// are no longer part of the updated code.

/* ========================================= */
/* ===== REDESIGNED FILTER SECTION END ===== */
/* ========================================= */

/* =============== SIDEBAR/DETAILS (unchanged layout) =============== */
const Sidebar = styled.aside`
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: 12px;
  box-shadow: none;
  overflow: hidden;
  height: calc(100vh - 64px - 24px - 18px);
  display: flex;
  flex-direction: column;
  @media (max-width: 1024px) {
    border-radius: 0;
    border: none;
    box-shadow: none;
    background: ${theme.colors.background};
    height: calc(100vh - 64px - 56px);
    display: ${(p) => (p.selectedTask ? "none" : "flex")};
  }
`;
const ListHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 16px;
  border-bottom: 1px solid ${theme.colors.border};
  background: ${theme.colors.surface};
  font-weight: 600;
  color: ${theme.colors.textHeading};
  @media (max-width: 1024px) {
    padding: 14px 16px;
    background: ${theme.colors.background};
    border-bottom: none;
  }
`;
const List = styled.div`
  background: ${theme.colors.background};
  padding: 8px;
  @media (max-width: 1024px) {
    background: ${theme.colors.background};
    padding: 8px;
    gap: 8px;
  }
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 10px;
  &::-webkit-scrollbar {
    width: 5px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 10px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
  }
`;
const Item = styled.button`
  width: 100%;
  text-align: left;
  border: 1px solid transparent;
  background: ${theme.colors.surface};
  border-radius: 10px;
  padding: 16px 18px;
  cursor: pointer;
  position: relative;
  transition: border-color 0.15s ease, background 0.15s ease;
  box-shadow: none;
  font-family: ${theme.typography.fontFamily};

  &:hover {
    border-color: ${theme.colors.border};
    background: ${theme.colors.surfaceHover};
  }

  &:active {
    background: ${theme.colors.surfaceHover};
  }

  @media (max-width: 768px) {
    padding: 16px 18px;

    &:hover {
      border-color: ${theme.colors.primary};
      transform: none;
    }

    &:active {
      background: #f5f7fa;
    }
  }

  ${(p) =>
    p.selected &&
    css`
      background: ${theme.colors.primaryLight};
      border-color: ${theme.colors.primaryLight};
      box-shadow: none;

      &:hover {
        background: ${theme.colors.primaryLight};
      }
    `}
`;
const ItemTopRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
`;

const ItemRightSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
`;

const MobileChevron = styled.div`
  display: none;
  color: ${theme.colors.textMuted};
  opacity: 0.7;

  @media (max-width: 768px) {
    display: flex;
    align-items: center;
  }
`;

const ItemHeader = styled.div`
  flex: 1;
  min-width: 0;
`;

const ItemTitle = styled.div`
  font-weight: 600;
  color: #1e293b;
  font-size: 1rem;
  font-family: ${theme.typography.fontFamily};
  letter-spacing: -0.01em;
  line-height: 1.4;
  margin-bottom: 8px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;
  position: relative;

  &:hover {
    color: ${theme.colors.primary};
  }

  &[title]:hover::after {
    content: attr(title);
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    z-index: 1000;
    background: ${theme.colors.textHeading};
    color: white;
    padding: 8px 12px;
    border-radius: 6px;
    font-size: 0.875rem;
    box-shadow: ${theme.shadows.lg};
    white-space: normal;
    line-height: 1.4;
    -webkit-line-clamp: unset;
    -webkit-box-orient: unset;
    display: block;
    text-overflow: unset;
    overflow: visible;
    pointer-events: none;
    animation: fadeIn 0.2s ease;
  }
`;

const ItemSubtitle = styled.div`
  display: flex;
  align-items: center;
  font-size: 0.8125rem;
  color: #64748b;
  font-weight: 500;
  font-family: ${theme.typography.fontFamily};
  line-height: 1.3;
  gap: 6px;
`;

const TaskMetaSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 4px;
`;

const PriorityInfo = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
`;

const TaskCategory = styled.span`
  font-size: 0.75rem;
  font-weight: 600;
  color: ${theme.colors.textBody};
  background: #ffffff;
  border: 1px solid ${theme.colors.border};
  padding: 4px 8px;
  border-radius: 999px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
`;

const Price = styled.div`
  font-weight: 700;
  font-size: 1rem;
  font-family: ${theme.typography.fontFamily};
  color: #334155;
  flex-shrink: 0;
  text-align: right;
  letter-spacing: -0.02em;
  line-height: 1.4;
  margin-top: 1px;
`;

const ItemRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  align-items: center;
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 10px;
  border-radius: 6px;
  background: #ffffff;
  border: 1px solid ${theme.colors.border};
  font-weight: 500;
  font-size: 0.75rem;
  color: #475569;
  transition: all 0.15s ease;
  line-height: 1;

  svg {
    width: 12px;
    height: 12px;
    flex-shrink: 0;
    display: block;
  }

  &:hover {
    background: ${theme.colors.surfaceHover};
    color: #334155;
  }
`;

const PriceSection = styled.div`
  text-align: right;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
`;

const TimePosted = styled.div`
  font-size: 0.6875rem;
  color: #94a3b8;
  font-weight: 500;
`;

const MobileHiddenBackButton = styled.div`
  padding: 14px 18px;
  background: #fbfdff;
  
  @media (max-width: 1024px) {
    display: none;
  }
`;

const Details = styled.main`
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: 12px;
  box-shadow: none;
  height: calc(100vh - 64px - 24px - 18px);
  padding: 0;
  overflow-y: auto;
  animation: ${fadeIn} 0.25s ease;
  position: relative;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 10px;
  }

  @media (max-width: 1024px) {
    display: ${(p) => (p.selectedTask ? "block" : "none")};
    border-radius: 0;
    border-left: 0;
    border-right: 0;
    height: calc(100vh - 64px - 56px);
  }
`;
const StickyHead = styled.div`
  position: sticky;
  top: 0;
  background: ${theme.colors.surface};
  backdrop-filter: none;
  border-bottom: 1px solid ${theme.colors.border};
  margin-bottom: 32px;
  padding: 20px 24px;
  z-index: 10;
  box-shadow: none;

  a {
    text-decoration: none;
    color: #0f172a;
    font-size: 20px;
    font-weight: 600;
    letter-spacing: -0.025em;
    display: block;
    line-height: 1.3;
    margin-bottom: 14px;
    transition: color 0.2s ease;
  }

  a:hover {
    color: ${theme.colors.primary};
  }

  @media (max-width: 1024px) {
    background: ${theme.colors.surface};
    backdrop-filter: none;
    border-bottom: 1px solid ${theme.colors.border};
    padding: 20px 16px;
    margin-bottom: 24px;

    a {
      font-size: 20px;
      margin-bottom: 12px;
    }
  }

  @media (max-width: 640px) {
    padding: 16px 14px;
    margin-bottom: 20px;

    a {
      font-size: 18px;
      margin-bottom: 10px;
    }
  }
`;
const ContentArea = styled.div`
  padding: 0 36px 48px;
  @media (max-width: 1024px) {
    padding: 0 20px 32px;
  }
`;
const MobileBack = styled.button`
  display: none;
  @media (max-width: 1024px) {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: #fff;
    border: 1px solid ${theme.colors.border};
    color: ${theme.colors.textBody};
    padding: 8px 10px;
    border-radius: 999px;
    font-weight: 700;
    cursor: pointer;
  }
`;
const PosterRow = styled.div`
  margin-top: 6px;
  display: flex;
  align-items: center;
  gap: 10px;
  color: ${theme.colors.textMuted};
  font-size: 0.92rem;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
`;

const PosterInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;

  @media (max-width: 768px) {
    flex-wrap: wrap;
    gap: 8px;
  }
`;

const ManageTaskButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: ${theme.colors.primaryLight};
  color: ${theme.colors.primary};
  border: 1px solid ${theme.colors.primary};
  border-radius: 8px;
  padding: 7px 12px;
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  margin-left: auto;
  transition: all 0.15s ease;

  &:hover {
    background: ${theme.colors.primary};
    border-color: ${theme.colors.primary};
    color: #fff;
  }

  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
    padding: 10px 12px;
    margin-left: 0;
  }
`;
const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin: 32px 0;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
  }
`;
const SoftStat = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  background: #ffffff;
  border: 1px solid ${theme.colors.border};
  border-radius: 10px;
  padding: 10px 12px;

  .label {
    color: #94a3b8;
    font-weight: 500;
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 2px;
  }
  .value {
    color: ${theme.colors.textPrimary};
    font-weight: 700;
    font-size: 1.05rem;
  }
`;
const ButtonRow = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin: 20px 0 16px;
`;
const Primary = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: ${theme.colors.primary};
  color: #fff;
  border: 1px solid ${theme.colors.primary};
  border-radius: 8px;
  padding: 7px 10px;
  font-weight: 700;
  font-size: 0.9rem;
  cursor: pointer;
  box-shadow: none;
  transition: background 0.1s ease;
  &:hover {
    background: ${theme.colors.primaryHover};
  }
`;
const Ghost = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #fff;
  color: ${theme.colors.textBody};
  border: 1px solid ${theme.colors.border};
  border-radius: 8px;
  padding: 7px 10px;
  font-weight: 700;
  font-size: 0.9rem;
  cursor: pointer;
  transition: border 0.12s, background 0.12s;
  &:hover {
    background: ${theme.colors.primaryLight};
    border-color: ${theme.colors.primary};
    color: ${theme.colors.primaryText};
  }
`;
const Section = styled.section`
  padding: 20px 0 24px;
  animation: ${slideInUp} 0.2s ease;
  & + & {
    border-top: 1px solid ${theme.colors.border};
    margin-top: 8px;
    padding-top: 22px;
  }
`;
const SectionTitle = styled.h2`
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0 0 14px;
  font-size: 18px;
  font-weight: ${theme.typography.fontWeight.bold};
  font-family: ${theme.typography.fontFamily};
  color: ${theme.colors.textPrimary};
  letter-spacing: -0.02em;

  svg {
    color: ${theme.colors.primary};
    background: ${theme.colors.primaryLight};
    border-radius: 6px;
    padding: 4px;
    width: 22px;
    height: 22px;
  }
`;
const Body = styled.p`
  color: ${theme.colors.textBody};
  line-height: 1.75;
  margin: 0;
  font-size: 1rem;
  letter-spacing: -0.01em;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
`;
const TagWrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;
const TimePill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #ffffff;
  border: 1px solid ${theme.colors.border};
  color: ${theme.colors.textBody};
  padding: 6px 10px;
  border-radius: 999px;
  font-weight: 600;
  font-size: 0.85rem;
  letter-spacing: -0.01em;
  transition: all 0.2s ease;

  &:hover {
    background: ${theme.colors.primaryLight};
    border-color: ${theme.colors.primaryLight};
  }
`;
const Thumb = styled.img`
  height: 84px;
  width: 128px;
  object-fit: cover;
  border-radius: 10px;
  border: 1px solid ${theme.colors.border};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    transform: none;
    box-shadow: none;
    border-color: ${theme.colors.primary};
  }
`;
const Skeleton = styled.div`
  border-radius: 12px;
  background: linear-gradient(90deg, #f1f5f9, #f8fafc, #f1f5f9);
  background-size: 200% 100%;
  animation: shimmer 1.2s infinite linear;
  height: ${(p) => p.h || "14px"};
  @keyframes shimmer {
    from {
      background-position: 200% 0;
    }
    to {
      background-position: -200% 0;
    }
  }
`;
const Empty = styled.div`
  background: #ffffff;
  border: 1px solid ${theme.colors.border};
  border-radius: 10px;
  padding: 16px;
  margin: 20px 0;
  display: flex;
  flex-direction: column;
  gap: 16px;

  .content {
    width: 100%;

    .title {
      font-size: 15px;
      font-weight: 600;
      color: ${theme.colors.textHeading};
      margin: 0 0 8px 0;
    }

    .description {
      font-size: 14px;
      color: ${theme.colors.textMuted};
      margin: 0;
      line-height: 1.5;
    }
  }
`;

const EmptyStateButton = styled.button`
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 12px 16px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;

  &:hover {
    background: #2563eb;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

const NotificationBanner = styled.div`
  background: #ffffff;
  border: 1px solid ${theme.colors.border};
  border-radius: 10px;
  padding: 16px;
  margin: 20px 0;
  display: flex;
  flex-direction: column;
  gap: 16px;

  .content {
    width: 100%;

    .title {
      font-size: 15px;
      font-weight: 600;
      color: ${theme.colors.textHeading};
      margin: 0 0 8px 0;
    }

    .description {
      font-size: 14px;
      color: ${theme.colors.textMuted};
      margin: 0;
      line-height: 1.5;
    }
  }
`;

const NotificationButton = styled.button`
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;

  &:hover {
    background: #2563eb;
  }

  &:active {
    transform: none;
  }
`;

/* =============== DATA HOOK =============== */
const useBrowseTasksData = (initialPayload = null, userState = null) => {
  const [tasks, setTasks] = useState([]);
  const [selectedTask, setSelectedTask] = useState(null);
  const [offers, setOffers] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [userOffer, setUserOffer] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isOffersLoading, setIsOffersLoading] = useState(false);
  const [isQuestionsLoading, setIsQuestionsLoading] = useState(false);

  // Offer prefetch cache and timeouts
  const offersCache = useRef(new Map());
  const prefetchTimeouts = useRef(new Map());

  const fetchTasks = async (payload = {}) => {
    setIsLoading(true);
    const searchStartTime = Date.now();
    console.log("fetchTasks called with payload:", payload);
    try {
      const { data } = await request(
        "POST",
        API_CONSTANT_MAP.fetch_tasks,
        payload
      );
      if (data?.success) {
        const newTasks = data.tasks || [];
        setTasks(newTasks);
        const isDesktop =
          typeof window !== "undefined" && window.innerWidth > 1024;

        // Always auto-select first task on desktop when there are results
        if (newTasks.length > 0 && isDesktop) {
          setSelectedTask(newTasks[0]);
        } else if (newTasks.length === 0) {
          setSelectedTask(null);
        } else if (!isDesktop && selectedTask) {
          // On mobile, only keep selection if task still exists
          const stillExists = newTasks.find((t) => t.id === selectedTask.id);
          if (!stillExists) setSelectedTask(null);
        }

        // Track search for analytics (only for signed-in users)
        const searchDuration = Date.now() - searchStartTime;
        trackUserSearch(payload, newTasks.length, searchDuration);
      }
    } catch (err) {
      console.error("Failed to fetch tasks:", err);
    } finally {
      setIsLoading(false);
    }
  };

  // Track user search behavior for analytics
  const trackUserSearch = async (searchData, resultsCount, durationMs) => {
    try {
      // Only track meaningful searches (not initial page loads with default params)
      const hasSearchQuery = searchData.q && searchData.q.trim().length > 0;
      const hasFilters =
        searchData.task_type !== "all" ||
        searchData.zipcode ||
        searchData.min_budget ||
        searchData.max_budget ||
        searchData.sort_by !== "date_desc";

      if (!hasSearchQuery && !hasFilters) {
        return; // Skip tracking default/initial searches
      }

      await request("POST", API_CONSTANT_MAP.store_user_search, {
        search_data: searchData,
        results_count: resultsCount,
        search_duration_ms: durationMs,
        session_id:
          sessionStorage.getItem("searchSessionId") || generateSessionId(),
      });
    } catch (err) {
      // Silently fail - don't interrupt user experience for analytics
      console.warn("Failed to track search:", err);
    }
  };

  // Generate unique session ID for grouping searches
  const generateSessionId = () => {
    const sessionId = `search_${Date.now()}_${Math.random()
      .toString(36)
      .substr(2, 9)}`;
    sessionStorage.setItem("searchSessionId", sessionId);
    return sessionId;
  };

  // Prefetch offers on hover
  const prefetchOffers = useCallback((taskId) => {
    // Don't prefetch if already cached or currently selected
    if (offersCache.current.has(taskId)) {
      console.log(`🎯 Task ${taskId} already cached, skipping prefetch`);
      return;
    }
    if (selectedTask?.id === taskId) {
      console.log(`🎯 Task ${taskId} already selected, skipping prefetch`);
      return;
    }
    
    console.log(`🔄 Starting prefetch timer for task ${taskId}`);
    
    // Clear any existing timeout for this task
    const existingTimeout = prefetchTimeouts.current.get(taskId);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
    }
    
    // Debounce: only fetch if hover lasts 300ms
    const timeoutId = setTimeout(async () => {
      try {
        console.log(`🚀 Prefetching offers for task ${taskId}`);
        const { data } = await request("POST", API_CONSTANT_MAP.fetch_task_offers, {
          task_id: taskId
        });
        const offers = data?.success ? data.offers : [];
        offersCache.current.set(taskId, offers);
        console.log(`✅ Cached ${offers.length} offers for task ${taskId}`);
        // Cache for 5 minutes
        setTimeout(() => {
          console.log(`🗑️ Clearing cache for task ${taskId}`);
          offersCache.current.delete(taskId);
        }, 5 * 60 * 1000);
      } catch (err) {
        console.warn(`❌ Failed to prefetch offers for task ${taskId}:`, err);
      }
      prefetchTimeouts.current.delete(taskId);
    }, 300);
    
    prefetchTimeouts.current.set(taskId, timeoutId);
  }, [selectedTask]);
  
  const cancelPrefetch = useCallback((taskId) => {
    const timeoutId = prefetchTimeouts.current.get(taskId);
    if (timeoutId) {
      console.log(`🛑 Cancelling prefetch for task ${taskId}`);
      clearTimeout(timeoutId);
      prefetchTimeouts.current.delete(taskId);
    }
  }, []);

  useEffect(() => {
    if (!selectedTask) {
      // Clear data when no task is selected
      setOffers([]);
      setQuestions([]);
      setUserOffer(null);
      return;
    }

    // Clear previous data immediately to avoid showing stale content
    setOffers([]);
    setQuestions([]);
    setUserOffer(null);

    // Helper to find user's offer
    const findUserOffer = (offersList) => {
      if (userState?.user?.user_id) {
        return offersList.find(offer => offer.user?.id === userState.user.user_id) || null;
      }
      return null;
    };

    // Check cache first, then fetch if needed
    const cachedOffers = offersCache.current.get(selectedTask.id);
    if (cachedOffers) {
      console.log(`✅ Using cached offers for task ${selectedTask.id}`);
      setOffers(cachedOffers);
      setUserOffer(findUserOffer(cachedOffers));
      // Don't set loading to false here - it should already be false from previous state
    } else {
      console.log(`⏳ Fetching offers for task ${selectedTask.id} (not in cache)`);
      // Only show loading if not in cache
      setIsOffersLoading(true);
      request("POST", API_CONSTANT_MAP.fetch_task_offers, {
        task_id: selectedTask.id,
      })
        .then(({ data }) => {
          const offers = data?.success ? data.offers : [];
          setOffers(offers);
          setUserOffer(findUserOffer(offers));
          // Cache the result
          offersCache.current.set(selectedTask.id, offers);
          // Cache for 5 minutes
          setTimeout(() => offersCache.current.delete(selectedTask.id), 5 * 60 * 1000);
        })
        .catch((err) => console.error("Error fetching offers:", err))
        .finally(() => setIsOffersLoading(false));
    }

    // Fetch questions
    setIsQuestionsLoading(true);
    request("POST", API_CONSTANT_MAP.fetch_task_questions, {
      task_id: selectedTask.id,
    })
      .then(({ data }) => setQuestions(data?.success ? data.questions : []))
      .catch((err) => console.error("Error fetching questions:", err))
      .finally(() => setIsQuestionsLoading(false));
  }, [selectedTask]);

  const addNewOffer = (newOffer) => {
    // If this is the user's offer, update userOffer
    if (newOffer.user?.id === userState?.user?.user_id) {
      setUserOffer(newOffer);
      // Update existing offer in the list or add new
      setOffers(prev => {
        const existingIndex = prev.findIndex(o => o.id === newOffer.id);
        if (existingIndex >= 0) {
          const updated = [...prev];
          updated[existingIndex] = newOffer;
          return updated;
        }
        return [newOffer, ...prev];
      });
    } else {
      setOffers((prev) => [newOffer, ...prev]);
    }
  };
  const addNewQuestion = (newQuestion) =>
    setQuestions((prev) => [newQuestion, ...prev]);

  const refreshQuestions = async () => {
    if (!selectedTask) return;
    
    setIsQuestionsLoading(true);
    try {
      const { data } = await request("POST", API_CONSTANT_MAP.fetch_task_questions, {
        task_id: selectedTask.id,
      });
      setQuestions(data?.success ? data.questions : []);
    } catch (err) {
      console.error("Error refreshing questions:", err);
    } finally {
      setIsQuestionsLoading(false);
    }
  };

  return {
    tasks,
    selectedTask,
    setSelectedTask,
    offers,
    questions,
    userOffer,
    addNewOffer,
    addNewQuestion,
    refreshQuestions,
    isLoading,
    isOffersLoading,
    isQuestionsLoading,
    fetchTasks,
    prefetchOffers,
    cancelPrefetch,
  };
};

/* =============== OFFER FORM =============== */
// User's own offer card (compact inline style for ButtonRow - matches Primary button height)
const UserOfferCard = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #ffffff;
  color: #166534;
  border: 1px solid #86efac;
  border-radius: 10px;
  padding: 8px 10px;
  font-weight: 700;
  font-size: 0.9rem;
`;

const UserOfferTitle = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
  color: #166534;
`;

const UserOfferAmount = styled.span`
  font-weight: 700;
  color: #166534;
`;

const EditOfferButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  background: #f0fdf4;
  color: #166534;
  border: 1px solid #86efac;
  border-radius: 6px;
  padding: 3px 8px;
  font-weight: 600;
  font-size: 0.8rem;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  margin-left: 4px;

  &:hover {
    background: #166534;
    color: #ffffff;
  }
`;

// Edit button variant for use inside offer cards (green styling on white background)
const EditOfferCardButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: #f0fdf4;
  color: #166534;
  border: 1px solid #86efac;
  border-radius: 6px;
  padding: 5px 10px;
  font-weight: 600;
  font-size: 0.78rem;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    background: #166534;
    color: #ffffff;
    border-color: #166534;
  }
`;

const OfferFormCard = styled.div`
  background: ${theme.colors.surface};
  border-radius: 16px;
  border: 1px solid #93c5fd;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.1);
  animation: ${slideInUp} 0.25s ease;
`;
const FormField = styled.div`
  margin-bottom: 18px;
  label {
    display: block;
    font-size: 0.95rem;
    font-weight: 600;
    margin-bottom: 8px;
    color: ${theme.colors.textBody};

    /* Style required field asterisks */
    & > span.required {
      color: #dc2626;
      font-weight: 700;
      margin-left: 2px;
    }
  }
`;
const Input = styled.input`
  width: 100%;
  padding: 9px 12px;
  border-radius: 8px;
  border: 1px solid
    ${(props) => (props.$hasError ? "#ef4444" : theme.colors.border)};
  background: #fff;
  transition: all 0.2s ease;

  ${(props) =>
    props.$hasError &&
    css`
      animation: shake 0.5s ease-in-out;
    `}

  &:focus {
    outline: none;
    border-color: ${(props) =>
      props.$hasError ? "#ef4444" : theme.colors.primary};
    box-shadow: ${(props) =>
      props.$hasError ? "none" : `0 0 0 2px ${theme.colors.primaryLight}`};
  }

  @keyframes shake {
    0%,
    100% {
      transform: translateX(0);
    }
    25% {
      transform: translateX(-2px);
    }
    75% {
      transform: translateX(2px);
    }
  }
`;
const Textarea = styled(Input).attrs({ as: "textarea" })`
  resize: vertical;
  min-height: 110px;
`;
const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
  @media (max-width: 680px) {
    grid-template-columns: 1fr;
  }
`;
const ExperienceTag = styled.div`
  display: inline-flex;
  align-items: center;
  background: #ffffff;
  color: ${theme.colors.textBody};
  padding: 3px 8px;
  border-radius: 999px;
  font-weight: 600;
  font-size: 0.78rem;
  border: 1px solid ${theme.colors.border};
  white-space: nowrap;
`;
const OfferCard = styled.div`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 12px;
  animation: ${slideInUp} 0.2s ease;
  box-shadow: none;
  transition: border-color 0.2s ease;

  &:hover {
    border-color: #cbd5e1;
  }
`;
const OfferHeader = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
`;

const OfferHeaderMain = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  min-width: 0;
`;

const OfferHeaderSide = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
`;

const OfferHeaderTopRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const OfferStatusBadge = styled.div`
  display: inline-block;
  padding: 5px 8px;
  border-radius: 999px;
  font-weight: 600;
  font-size: 0.78rem;
  border: 1px solid ${theme.colors.border};
  background: ${props => 
    props.status === 'rejected' ? '#ffffff' : 
    props.status === 'accepted' ? '#ffffff' : 
    props.status === 'withdrawn' ? '#ffffff' : 
    '#ffffff'
  };
  color: ${props => 
    props.status === 'rejected' ? '#64748b' : 
    props.status === 'accepted' ? '#166534' : 
    props.status === 'withdrawn' ? '#b45309' : 
    theme.colors.textBody
  };
`;

const TaskProgressContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const TaskProgressPill = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  border-radius: 16px;
  font-weight: 600;
  font-size: 0.8rem;
  transition: all 0.2s ease;
  width: fit-content;
  background: ${props => 
    '#ffffff'
  };
  color: ${props => 
    props.status === 'open' ? '#0369a1' : 
    props.status === 'in_progress' ? '#b45309' : 
    props.status === 'completed' ? '#166534' : 
    '#64748b'
  };
`;

const TaskProgressConnector = styled.div`
  width: 12px;
  height: 2px;
  background: ${props => props.completed ? '#10b981' : '#e5e7eb'};
  transition: background-color 0.2s ease;
`;

const OfferPrice = styled.div`
  margin-left: auto;
  font-weight: 800;
  font-size: 1.25rem;
  color: ${theme.colors.textSecondary};
  padding: 8px 14px;
  border-radius: 10px;
  letter-spacing: -0.02em;
`;

const OfferUserInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const OfferUserName = styled.div`
  font-weight: 700;
  font-size: 1rem;
  color: ${theme.colors.textPrimary};
  margin-bottom: 2px;
  display: flex;
  align-items: center;
  gap: 8px;
  
  a {
    color: inherit;
    text-decoration: none;
    cursor: pointer;
    transition: color 0.2s ease;
    
    &:hover {
      color: ${theme.colors.primary};
    }
  }
`;

const OfferTimestamp = styled.div`
  font-size: 0.85rem;
  color: ${theme.colors.textMuted};
`;

const OfferProposal = styled.div`
  color: #334155;
  line-height: 1.7;
  font-size: 0.95rem;
  padding: 12px;
  background: #ffffff;
  border: 1px solid ${theme.colors.border};
  border-radius: 8px;
  margin-bottom: 12px;
`;

const OfferSection = styled.div`
  margin-bottom: 16px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const OfferSectionTitle = styled.div`
  font-weight: 700;
  font-size: 0.9rem;
  color: ${theme.colors.textPrimary};
  margin-bottom: 6px;
`;

const OfferSectionContent = styled.div`
  font-size: 0.92rem;
  color: ${theme.colors.textMuted};
  line-height: 1.5;
`;

const OfferMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 16px;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid ${theme.colors.border};
`;

const OfferChat = styled.div`
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px dashed ${theme.colors.border};
`;

const OfferChatHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  font-weight: 600;
  color: ${theme.colors.textHeading};
  font-size: 0.95rem;
`;

const OfferChatCount = styled.span`
  color: ${theme.colors.textMuted};
  font-size: 0.85rem;
  font-weight: 500;
`;

const OfferChatEmpty = styled.div`
  color: ${theme.colors.textMuted};
  font-size: 0.85rem;
  margin-bottom: 6px;
`;

const OfferChatLink = styled(Link)`
  color: ${theme.colors.primaryText};
  text-decoration: none;
  font-weight: 600;

  &:hover {
    text-decoration: underline;
  }
`;

const OfferChatMessages = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 8px;
`;

const OfferChatToggle = styled.button`
  background: none;
  border: 0;
  padding: 0;
  color: ${theme.colors.primaryText};
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  margin-left: auto;

  &:hover {
    text-decoration: underline;
  }
`;

const OfferChatMessage = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-start;
`;

const OfferChatBubble = styled.div`
  background: #ffffff;
  border: 1px solid ${theme.colors.border};
  border-radius: 10px;
  padding: 6px 8px;
  max-width: 520px;
`;

const OfferChatMeta = styled.div`
  display: flex;
  gap: 6px;
  align-items: center;
  font-size: 0.8rem;
  color: ${theme.colors.textMuted};
  margin-bottom: 4px;
`;

const OfferChatText = styled.div`
  font-size: 0.85rem;
  color: ${theme.colors.textBody};
  line-height: 1.35;
`;

const OfferMetaItem = styled.div`
  .label {
    color: ${theme.colors.textMuted};
    font-weight: 600;
    font-size: 0.85rem;
    margin-bottom: 4px;
  }

  .value {
    font-weight: 700;
    color: ${theme.colors.textBody};
    font-size: 0.9rem;
  }
`;

const PortfolioLinks = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
`;

const PortfolioLinksTitle = styled(OfferSectionTitle)`
  display: flex;
  align-items: center;
  gap: 6px;

  svg {
    width: 14px;
    height: 14px;
    color: #64748b;
  }
`;

const PortfolioLinksList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const PortfolioLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: transparent;
  color: ${theme.colors.textBody};
  padding: 6px 12px;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 500;
  text-decoration: none;
  transition: all 0.2s ease;
  border: 1px solid #e2e8f0;

  &:hover {
    background: ${theme.colors.primaryLight};
    border-color: ${theme.colors.primary};
    text-decoration: none;
  }

  svg {
    width: 14px;
    height: 14px;
    flex-shrink: 0;
  }
`;

const PortfolioLinkIcon = styled.div`
  display: none;
`;

const PortfolioLinkText = styled.div`
  flex: 1;
  min-width: 0;
`;

const PortfolioLinkDomain = styled.div`
  font-weight: 600;
  font-size: 0.9rem;
  color: #1e293b;
  margin-bottom: 2px;
`;

const PortfolioLinkUrl = styled.div`
  font-size: 0.8rem;
  color: #2563eb;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const PortfolioLinkArrow = styled.div`
  color: #94a3b8;
  flex-shrink: 0;

  svg {
    width: 16px;
    height: 16px;
  }
`;

const QuestionCard = styled.div`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 14px;
  margin-bottom: 12px;
  animation: ${slideInUp} 0.2s ease;
  transition: border-color 0.2s ease;

  &:hover {
    border-color: #cbd5e1;
  }
`;

const QuestionHeader = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-start;
  margin-bottom: 16px;
`;

const QuestionUserInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const QuestionUserName = styled.div`
  font-weight: 600;
  font-size: 0.95rem;
  color: #0f172a;
  display: flex;
  align-items: center;
  gap: 6px;

  a {
    color: inherit;
    text-decoration: none;
    &:hover {
      color: ${theme.colors.primary};
    }
  }
`;

/* =============== REPORT BUTTON =============== */
const ReportButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: transparent;
  color: #94a3b8;
  border: none;
  padding: 4px 8px;
  font-weight: 500;
  font-size: 0.8rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    color: #64748b;
    text-decoration: underline;
  }

  svg {
    width: 12px;
    height: 12px;
  }
`;

const OfferMenu = styled.div`
  position: relative;
`;

const OfferMenuButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: #ffffff;
  color: #64748b;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: #cbd5e1;
    color: #475569;
  }
`;

const OfferMenuDropdown = styled.div`
  position: absolute;
  top: 34px;
  right: 0;
  min-width: 140px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  box-shadow: ${theme.shadows.sm};
  overflow: hidden;
  z-index: 5;
`;

const OfferMenuItem = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  background: transparent;
  border: 0;
  text-align: left;
  font-size: 0.9rem;
  color: #0f172a;
  cursor: pointer;

  &:hover {
    background: #f8fafc;
  }
`;

/* =============== REPORT MODAL =============== */
const ReportModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 500000;
`;

const ReportModalContent = styled.div`
  background: white;
  border-radius: 12px;
  padding: 16px;
  max-width: 400px;
  width: 90%;
  margin: 20px;
  box-shadow: ${theme.shadows.md};
`;

const ReportModalTitle = styled.h3`
  margin: 0 0 16px;
  font-size: 1.25rem;
  font-weight: 700;
  color: #111827;
`;

const ReportTypeOption = styled.div`
  padding: 12px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  margin-bottom: 8px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #f9fafb;
    border-color: #d1d5db;
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

const ReportOptionTitle = styled.div`
  font-weight: 600;
  color: #374151;
  margin-bottom: 4px;
`;

const ReportOptionDescription = styled.div`
  font-size: 0.875rem;
  color: #6b7280;
`;

const ReportModalButtons = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 20px;
`;

const ReportModalButton = styled.button`
  flex: 1;
  padding: 8px 12px;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &.cancel {
    background: #f3f4f6;
    border: 1px solid #d1d5db;
    color: #374151;

    &:hover {
      background: #e5e7eb;
    }
  }

  &.submit {
    background: #dc2626;
    border: 1px solid #dc2626;
    color: white;

    &:hover {
      background: #b91c1c;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
`;

/* =============== FLOATING ACTION BUTTON =============== */
const FloatingActionButton = styled.button`
  position: fixed;
  bottom: 24px;
  right: 24px;
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: ${theme.colors.primary};
  border: none;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: none;
  transition: background 0.2s ease;
  z-index: 1000;
  font-size: 0;

  &:hover {
    background: ${theme.colors.primaryHover};
  }

  &:active {
    transform: none;
  }

  @media (max-width: 768px) {
    bottom: 90px; /* Above mobile nav if present */
    width: 56px;
    height: 56px;
  }

  @media (max-width: 480px) {
    bottom: 80px;
    right: 20px;
    width: 52px;
    height: 52px;
  }
`;

const FloatingButtonIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease;

  ${FloatingActionButton}:hover & {
    transform: none;
  }
`;

const QuestionTimestamp = styled.div`
  font-size: 0.8rem;
  color: #64748b;
  display: flex;
  align-items: center;
  margin-top: 4px;
`;

const QuestionText = styled.div`
  color: #334155;
  line-height: 1.7;
  font-size: 0.95rem;
  padding: 12px;
  background: #ffffff;
  border: 1px solid ${theme.colors.border};
  border-radius: 8px;
  margin-bottom: 12px;
`;

const QuestionIcon = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${theme.colors.textMuted};
  flex-shrink: 0;
`;

const RepliesContainer = styled.div`
  margin-top: 0;
  margin-left: 48px;
  padding: 12px;
  background: #ffffff;
  border-radius: 8px;
  border: 1px solid ${theme.colors.border};
`;

const ReplyItem = styled.div`
  margin-bottom: 16px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const ReplyHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
`;

const ReplyUserName = styled.div`
  font-weight: 600;
  font-size: 0.875rem;
  color: #0f172a;
  cursor: pointer;
  &:hover {
    color: #3b82f6;
  }
`;

const ReplyTimestamp = styled.div`
  font-size: 0.75rem;
  color: #64748b;
  display: flex;
  align-items: center;
`;

const ReplyText = styled.div`
  font-size: 0.85rem;
  color: #334155;
  line-height: 1.6;
`;

// Reply form styled components (matching ManageTask)
const ReplyForm = styled.div`
  margin-top: 12px;
  padding: 12px;
  background: #ffffff;
  border-radius: 10px;
  border: 1px solid ${theme.colors.border};
`;

const ReplyTextarea = styled.textarea`
  width: 100%;
  min-height: 80px;
  padding: 10px;
  border: 1px solid ${theme.colors.border};
  border-radius: 8px;
  font-family: inherit;
  font-size: 0.9rem;
  resize: vertical;
  margin-bottom: 10px;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
    box-shadow: 0 0 0 2px ${theme.colors.primaryLight};
  }

  &::placeholder {
    color: ${theme.colors.textMuted};
  }
`;

const ReplyActions = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
`;

const ReplyButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 6px;
  font-weight: 600;
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.12s ease;

  &.primary {
    background: ${theme.colors.primary};
    color: white;
    border: 1px solid ${theme.colors.primary};

    &:hover {
      background: ${theme.colors.primaryHover};
    }
  }

  &.secondary {
    background: white;
    color: ${theme.colors.textBody};
    border: 1px solid ${theme.colors.border};

    &:hover {
      background: ${theme.colors.primaryLight};
      color: ${theme.colors.primary};
    }
  }
`;

const SecondaryButton = Ghost;
const PrimaryButton = Primary;

const QuestionFormComponent = ({ task, onCancel, onQuestionSubmitted }) => {
  const [questionText, setQuestionText] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasError, setHasError] = useState(false);

  const handleSubmit = async () => {
    if (!questionText.trim()) {
      setHasError(true);
      return;
    }
    setHasError(false);
    setIsSubmitting(true);
    try {
      const payload = {
        task_id: task.id,
        question_text: questionText.trim(),
      };
      const { data } = await request(
        "POST",
        API_CONSTANT_MAP.submit_task_question,
        payload
      );
      if (data?.success) {
        onQuestionSubmitted(data.question);
        onCancel();
      } else {
        console.error(
          "Question submission error:",
          data.error?.message || "Unknown error"
        );
      }
    } catch (error) {
      console.error("Failed to submit question:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <OfferFormCard>
      <SectionTitle>Ask a Question</SectionTitle>
      <FormField>
        <Textarea
          value={questionText}
          onChange={(e) => {
            setQuestionText(e.target.value);
            if (hasError) setHasError(false);
          }}
          placeholder="What would you like to know about this task?"
          style={{ minHeight: "120px" }}
          $hasError={hasError}
        />
      </FormField>
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 10,
          marginTop: 10,
        }}
      >
        <SecondaryButton type="button" onClick={onCancel}>
          Cancel
        </SecondaryButton>
        <PrimaryButton
          type="button"
          onClick={handleSubmit}
          disabled={isSubmitting}
        >
          {isSubmitting ? "Submitting..." : "Submit Question"}
        </PrimaryButton>
      </div>
    </OfferFormCard>
  );
};

const OfferFormComponent = ({ task, onCancel, onOfferSubmitted, existingOffer }) => {
  const [formData, setFormData] = useState({
    rate: existingOffer?.offer_amount?.toString() || "",
    proposal: existingOffer?.proposal || "",
    duration: existingOffer?.duration || "",
    completionDate: existingOffer?.completion_date || "",
    yearsExperience: existingOffer?.years_experience?.toString() || "",
    previousWork: existingOffer?.previous_similar_work || "",
    portfolioLinks: existingOffer?.portfolio_links?.length > 0 ? existingOffer.portfolio_links : [""],
    clientQuestions: existingOffer?.client_questions || "",
  });
  const isEditing = !!existingOffer;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [submitError, setSubmitError] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));

    // Clear validation error for this field when user starts typing
    if (validationErrors[name]) {
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };
  const handlePortfolioLinkChange = (i, v) => {
    const links = [...formData.portfolioLinks];
    links[i] = v;
    setFormData((p) => ({ ...p, portfolioLinks: links }));
  };
  const addPortfolioLink = () =>
    setFormData((p) => ({ ...p, portfolioLinks: [...p.portfolioLinks, ""] }));

  const handleSubmit = async () => {
    // Validate required fields
    const errors = {};
    if (!formData.rate || formData.rate.trim() === "") {
      errors.rate = true;
    }
    if (!formData.proposal || formData.proposal.trim() === "") {
      errors.proposal = true;
    }

    setValidationErrors(errors);

    if (Object.keys(errors).length > 0) {
      setSubmitError("Budget and Proposal are required.");
      return;
    }
    setSubmitError("");
    setIsSubmitting(true);
    try {
      const payload = {
        task_id: task.id,
        offer_amount: formData.rate,
        proposal: formData.proposal,
        duration: formData.duration,
        completion_date: formData.completionDate,
        years_experience: formData.yearsExperience,
        previous_similar_work: formData.previousWork,
        portfolio_links: formData.portfolioLinks.filter((x) => x.trim()),
        client_questions: formData.clientQuestions,
      };
      const { data } = await request(
        "POST",
        API_CONSTANT_MAP.create_task_offer,
        payload
      );
      if (data?.success) {
        onOfferSubmitted(data.offer);
        onCancel();
      } else {
        setSubmitError(
          data.error?.message || "Failed to submit offer. Please try again."
        );
        console.error(
          "Offer submission error:",
          data.error?.message || "Unknown error"
        );
      }
    } catch (error) {
      setSubmitError("Failed to submit offer. Please try again.");
      console.error("Failed to submit offer:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <OfferFormCard>
      <SectionTitle>{isEditing ? "Edit Your Offer" : "Your Proposal"}</SectionTitle>

      <FormField>
        <label>
          Your Proposed Rate ($) <span className="required">*</span>
        </label>
        <Input
          type="number"
          name="rate"
          value={formData.rate}
          onChange={handleChange}
          placeholder={`Client's budget is $${task.budget}${task.budget_type === 'hourly' ? '/hr' : ''}`}
          $hasError={validationErrors.rate}
          required
        />
      </FormField>
      <FormField>
        <label>
          Why are you the best fit? <span className="required">*</span>
        </label>
        <Textarea
          name="proposal"
          value={formData.proposal}
          onChange={handleChange}
          placeholder="Highlight your relevant skills and experience..."
          $hasError={validationErrors.proposal}
          required
        />
      </FormField>

      {/* Advanced Options - Collapsible */}
      <details style={{ marginTop: '16px' }}>
        <summary style={{
          cursor: 'pointer',
          fontSize: '14px',
          fontWeight: '600',
          color: theme.colors.textMuted,
          marginBottom: '12px',
          padding: '8px 0',
          borderTop: `1px solid ${theme.colors.border}`,
          paddingTop: '16px'
        }}>
          Additional Details (Optional)
        </summary>
        
        <div style={{ paddingTop: '12px' }}>
          <FormRow>
            <FormField>
              <label>Estimated Duration</label>
              <Input
                name="duration"
                value={formData.duration}
                onChange={handleChange}
                placeholder="e.g. 2 Weeks"
              />
            </FormField>
            <FormField>
              <label>Proposed Completion Date</label>
              <Input
                name="completionDate"
                value={formData.completionDate}
                onChange={handleChange}
                type="date"
              />
            </FormField>
          </FormRow>
          
          <FormField>
            <label>Years of Relevant Experience</label>
            <Input
              type="number"
              name="yearsExperience"
              value={formData.yearsExperience}
              onChange={handleChange}
              placeholder="e.g., 5"
            />
          </FormField>
          
          <FormField>
            <label>Describe Previous Similar Work</label>
            <Textarea
              name="previousWork"
              value={formData.previousWork}
              onChange={handleChange}
              placeholder="Briefly describe 1–2 past projects."
            />
          </FormField>
          
          <FormField>
            <label>Portfolio / Work Samples</label>
            {formData.portfolioLinks.map((link, i) => (
              <Input
                key={i}
                value={link}
                onChange={(e) => handlePortfolioLinkChange(i, e.target.value)}
                placeholder="https://your-portfolio.com"
                style={{ marginBottom: 8 }}
              />
            ))}
            <button
              onClick={addPortfolioLink}
              type="button"
              style={{
                background: "none",
                border: 0,
                cursor: "pointer",
                color: theme.colors.primaryText,
                fontWeight: 700,
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                fontSize: ".9rem",
              }}
            >
              <PlusCircle size={16} /> Add another link
            </button>
          </FormField>
          
          <FormField>
            <label>Questions for the Client (optional)</label>
            <Textarea
              name="clientQuestions"
              value={formData.clientQuestions}
              onChange={handleChange}
              placeholder="Anything you need clarified?"
            />
          </FormField>
        </div>
      </details>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 12,
          marginTop: 20,
          paddingTop: 16,
          borderTop: `1px solid ${theme.colors.border}`,
        }}
      >
        {submitError && (
          <span style={{ color: "#ef4444", fontSize: "14px", flex: 1 }}>
            {submitError}
          </span>
        )}
        <div
          style={{
            display: "flex",
            gap: 12,
            marginLeft: submitError ? "auto" : "0",
          }}
        >
          <SecondaryButton type="button" onClick={onCancel}>
            Cancel
          </SecondaryButton>
          <PrimaryButton
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? (isEditing ? "Updating..." : "Submitting...") : (isEditing ? "Update Offer" : "Submit Proposal")}
          </PrimaryButton>
        </div>
      </div>
    </OfferFormCard>
  );
};
/* =============== MAIN PAGE =============== */
const BrowseTasksPage = ({ userState }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Ref for right panel to enable scroll-to-top on task change
  const detailsPanelRef = useRef(null);

  // Filters
  const initialQuery = searchParams.get("query") || searchParams.get("q") || "";
  const initialTaskType = searchParams.get("task_type") || "all";
  const initialCategory = searchParams.get("category") || "";
  const initialZipcode = searchParams.get("zipcode") || "";
  const initialRadius = searchParams.get("radius") ? parseInt(searchParams.get("radius")) : 25;
  const initialMinBudget = searchParams.get("min_budget") || "";
  const initialMaxBudget = searchParams.get("max_budget") || "";

  const [query, setQuery] = useState(initialQuery);
  const [taskType, setTaskType] = useState(initialTaskType); // all | remote | in-person
  const [zipcode, setZipcode] = useState(initialZipcode);
  const [radius, setRadius] = useState(initialRadius); // miles
  const [userLocation, setUserLocation] = useState(null); // {lat, lng} or null
  const [minBudget, setMinBudget] = useState(initialMinBudget);
  const [maxBudget, setMaxBudget] = useState(initialMaxBudget);
  const [selectedCategory, setSelectedCategory] = useState(initialCategory); // category filter
  const [categories, setCategories] = useState([]); // available categories
  const [sortBy, setSortBy] = useState("date_desc"); // date_desc|date_asc|budget_desc|budget_asc

  // Filters UI
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);

  // LinkedIn-style dropdown states
  const [showBudgetDropdown, setShowBudgetDropdown] = useState(false);
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);

  // Refs for click outside detection
  const budgetDropdownRef = useRef(null);
  const locationDropdownRef = useRef(null);
  const categoryDropdownRef = useRef(null);
  const mobileSheetRef = useRef(null);
  const mobileButtonRef = useRef(null);

  // FIXED: Moved useRef to the top level of the component
  const isInitialMount = useRef(true);

  // Image modal state
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [modalImages, setModalImages] = useState([]);

  // Report modal state
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [reportEntity, setReportEntity] = useState({ type: null, id: null });
  const [selectedReportType, setSelectedReportType] = useState(null);
  const [isSubmittingReport, setIsSubmittingReport] = useState(false);

  // Image modal handlers
  const openImageModal = (images, index = 0) => {
    setModalImages(images);
    setCurrentImageIndex(index);
    setIsImageModalOpen(true);
  };

  const closeImageModal = () => {
    setIsImageModalOpen(false);
    setModalImages([]);
    setCurrentImageIndex(0);
  };

  const handleNextImage = (newIndex) => {
    setCurrentImageIndex(newIndex);
  };

  const handlePreviousImage = (newIndex) => {
    setCurrentImageIndex(newIndex);
  };

  // Handle click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        budgetDropdownRef.current &&
        !budgetDropdownRef.current.contains(event.target)
      ) {
        setShowBudgetDropdown(false);
      }
      if (
        locationDropdownRef.current &&
        !locationDropdownRef.current.contains(event.target)
      ) {
        setShowLocationDropdown(false);
      }
      if (
        categoryDropdownRef.current &&
        !categoryDropdownRef.current.contains(event.target)
      ) {
        setShowCategoryDropdown(false);
      }
      if (
        mobileSheetRef.current &&
        !mobileSheetRef.current.contains(event.target) &&
        mobileButtonRef.current &&
        !mobileButtonRef.current.contains(event.target)
      ) {
        setIsFiltersOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Tabs + offer form + question form
  const [activeTab, setActiveTab] = useState("qa");
  const [showOfferForm, setShowOfferForm] = useState(false);
  const [showQuestionForm, setShowQuestionForm] = useState(false);
  
  // Reply states (matching ManageTask)
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [replySubmitting, setReplySubmitting] = useState(false);
  const [preventTabSwitch, setPreventTabSwitch] = useState(false);

  // Edit question states
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [editQuestionText, setEditQuestionText] = useState("");
  const [editSubmitting, setEditSubmitting] = useState(false);

  // Edit reply states
  const [editingReply, setEditingReply] = useState(null);
  const [editReplyText, setEditReplyText] = useState("");
  const [editReplySubmitting, setEditReplySubmitting] = useState(false);

  // Offer chat states (per-offer)
  const [offerQuestionsById, setOfferQuestionsById] = useState({});
  const [offerQuestionsLoadingById, setOfferQuestionsLoadingById] = useState({});
  const [offerQuestionTextById, setOfferQuestionTextById] = useState({});
  const [offerQuestionSubmittingById, setOfferQuestionSubmittingById] = useState({});
  const [offerReplyingToById, setOfferReplyingToById] = useState({});
  const [offerReplyTextById, setOfferReplyTextById] = useState({});
  const [offerReplySubmittingById, setOfferReplySubmittingById] = useState({});
  const [offerChatExpandedById, setOfferChatExpandedById] = useState({});
  const [offerMenuOpenById, setOfferMenuOpenById] = useState({});

  const hasUrlParams = initialQuery || initialTaskType !== "all";

  // Build initial payload for hook if URL params exist
  const initialPayload = hasUrlParams
    ? {
        q: initialQuery,
        task_type: initialTaskType,
        sort_by: "date_desc",
      }
    : null;

  const {
    tasks,
    selectedTask,
    setSelectedTask,
    offers,
    questions,
    userOffer,
    addNewOffer,
    addNewQuestion,
    refreshQuestions,
    isLoading,
    isOffersLoading,
    isQuestionsLoading,
    fetchTasks,
    prefetchOffers,
    cancelPrefetch,
  } = useBrowseTasksData(initialPayload, userState);

  const loadOfferQuestions = useCallback(async (offerId) => {
    if (!selectedTask || !offerId) return;
    setOfferQuestionsLoadingById((p) => ({ ...p, [offerId]: true }));
    try {
      const { data } = await request("POST", API_CONSTANT_MAP.fetch_task_questions, {
        task_id: selectedTask.id,
        offer_id: offerId,
      });
      setOfferQuestionsById((p) => ({
        ...p,
        [offerId]: data?.success ? data.questions : [],
      }));
    } catch (err) {
      console.error("Error fetching offer questions:", err);
    } finally {
      setOfferQuestionsLoadingById((p) => ({ ...p, [offerId]: false }));
    }
  }, [selectedTask]);

  // Ensure we start with left view (no selected task) when navigating to page
  // Use location.key to trigger reset on navigation changes
  const location = useLocation();
  useEffect(() => {
    setSelectedTask(null);
  }, [location.key]); // Reset when navigation changes (different from normal page refresh)

  // Scroll to top when task changes
  useEffect(() => {
    if (selectedTask && detailsPanelRef.current) {
      detailsPanelRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [selectedTask]);

  useEffect(() => {
    setOfferQuestionsById({});
    setOfferQuestionsLoadingById({});
    setOfferQuestionTextById({});
    setOfferQuestionSubmittingById({});
    setOfferReplyingToById({});
    setOfferReplyTextById({});
    setOfferReplySubmittingById({});
    setOfferChatExpandedById({});
    setOfferMenuOpenById({});
  }, [selectedTask?.id]);

  useEffect(() => {
    if (!selectedTask || !offers?.length) return;
    offers.forEach((offer) => {
      if (!offer?.id) return;
      if (offerQuestionsById[offer.id]) return;
      if (offerQuestionsLoadingById[offer.id]) return;
      loadOfferQuestions(offer.id);
    });
  }, [offers, selectedTask, offerQuestionsById, offerQuestionsLoadingById, loadOfferQuestions]);

  // Smart default tab based on available content
  useEffect(() => {
    if (selectedTask && !isOffersLoading && !isQuestionsLoading && !preventTabSwitch) {
      const hasQuestions = questions && questions.length > 0;
      const hasOffers = offers && offers.length > 0;
      
      // Priority: Offers > Questions > Default to questions tab
      if (hasOffers) {
        setActiveTab("offers");
      } else if (hasQuestions) {
        setActiveTab("qa");
      }
      // If neither has content, stay on questions tab (default for new tasks)
    }
  }, [selectedTask, offers, questions, isOffersLoading, isQuestionsLoading, preventTabSwitch]);

  // Reply functionality (matching ManageTask)
  const handleReplySubmit = async (questionId) => {
    if (!replyText.trim() || replySubmitting) return;

    try {
      setReplySubmitting(true);
      setPreventTabSwitch(true); // Prevent tab switching during reply submission
      
      const response = await request(
        "POST",
        API_CONSTANT_MAP.post_task_question_reply,
        {
          question_id: questionId,
          reply_text: replyText.trim(),
        }
      );

      if (response.data?.success) {
        // Refresh only questions to show the new reply (preserves page position and tab)
        await refreshQuestions();

        // Reset reply form
        setReplyingTo(null);
        setReplyText("");
      } else {
        alert("Failed to submit reply. Please try again.");
      }
    } catch (error) {
      console.error("Error submitting reply:", error);
      alert("Error submitting reply. Please try again.");
    } finally {
      setReplySubmitting(false);
      // Re-enable tab switching after a short delay to allow questions to update
      setTimeout(() => setPreventTabSwitch(false), 100);
    }
  };

  const handleOfferQuestionSubmit = async (offerId) => {
    const text = (offerQuestionTextById[offerId] || "").trim();
    if (!text || offerQuestionSubmittingById[offerId]) return;
    try {
      setOfferQuestionSubmittingById((p) => ({ ...p, [offerId]: true }));
      const payload = {
        task_id: selectedTask.id,
        offer_id: offerId,
        question_text: text,
      };
      const { data } = await request(
        "POST",
        API_CONSTANT_MAP.submit_task_question,
        payload
      );
      if (data?.success) {
        setOfferQuestionsById((p) => ({
          ...p,
          [offerId]: [data.question, ...(p[offerId] || [])],
        }));
        setOfferQuestionTextById((p) => ({ ...p, [offerId]: "" }));
      } else {
        alert(data?.error?.message || "Failed to post message.");
      }
    } catch (error) {
      console.error("Failed to submit offer message:", error);
      alert("Failed to submit message. Please try again.");
    } finally {
      setOfferQuestionSubmittingById((p) => ({ ...p, [offerId]: false }));
    }
  };

  const handleOfferReplySubmit = async (offerId, questionId) => {
    const text = (offerReplyTextById[offerId] || "").trim();
    if (!text || offerReplySubmittingById[offerId]) return;
    try {
      setOfferReplySubmittingById((p) => ({ ...p, [offerId]: true }));
      const response = await request(
        "POST",
        API_CONSTANT_MAP.post_task_question_reply,
        {
          question_id: questionId,
          reply_text: text,
        }
      );
      if (response.data?.success) {
        await loadOfferQuestions(offerId);
        setOfferReplyingToById((p) => ({ ...p, [offerId]: null }));
        setOfferReplyTextById((p) => ({ ...p, [offerId]: "" }));
      } else {
        alert("Failed to submit reply. Please try again.");
      }
    } catch (error) {
      console.error("Error submitting offer reply:", error);
      alert("Error submitting reply. Please try again.");
    } finally {
      setOfferReplySubmittingById((p) => ({ ...p, [offerId]: false }));
    }
  };

  // Edit question handlers
  const handleEditQuestion = (question) => {
    setEditingQuestion(question.id);
    setEditQuestionText(question.question_text || question.text);
    // Close reply form if open
    setReplyingTo(null);
    setReplyText("");
  };

  const handleEditSubmit = async (questionId) => {
    if (!editQuestionText.trim() || editSubmitting) return;

    try {
      setEditSubmitting(true);
      setPreventTabSwitch(true);
      const response = await request(
        "POST",
        API_CONSTANT_MAP.edit_task_question,
        {
          question_id: questionId,
          question_text: editQuestionText.trim(),
        }
      );

      if (response.data?.success) {
        await refreshQuestions();
        setEditingQuestion(null);
        setEditQuestionText("");
      } else {
        alert(response.data?.error?.message || "Failed to update question. Please try again.");
      }
    } catch (error) {
      console.error("Error updating question:", error);
      alert("Error updating question. Please try again.");
    } finally {
      setEditSubmitting(false);
      setTimeout(() => setPreventTabSwitch(false), 100);
    }
  };

  const handleCancelEdit = () => {
    setEditingQuestion(null);
    setEditQuestionText("");
  };

  // Edit reply handlers
  const handleEditReply = (reply) => {
    setEditingReply(reply.id);
    setEditReplyText(reply.reply_text || reply.text);
    // Close question edit form if open
    setEditingQuestion(null);
    setEditQuestionText("");
  };

  const handleEditReplySubmit = async (replyId) => {
    if (!editReplyText.trim() || editReplySubmitting) return;

    try {
      setEditReplySubmitting(true);
      setPreventTabSwitch(true);
      const response = await request(
        "POST",
        API_CONSTANT_MAP.edit_task_question_reply,
        {
          reply_id: replyId,
          reply_text: editReplyText.trim(),
        }
      );

      if (response.data?.success) {
        await refreshQuestions();
        setEditingReply(null);
        setEditReplyText("");
      } else {
        alert(response.data?.error?.message || "Failed to update reply. Please try again.");
      }
    } catch (error) {
      console.error("Error updating reply:", error);
      alert("Error updating reply. Please try again.");
    } finally {
      setEditReplySubmitting(false);
      setTimeout(() => setPreventTabSwitch(false), 100);
    }
  };

  const handleCancelEditReply = () => {
    setEditingReply(null);
    setEditReplyText("");
  };

  // Initialize categories (constant or API call)
  useEffect(() => {
    if (USE_CATEGORIES_CONSTANT) {
      // Use constant categories for instant loading
      setCategories(CATEGORIES);
    } else {
      // Fetch categories from API (original behavior)
      const fetchCategories = async () => {
        try {
          const { data } = await request(
            "POST",
            API_CONSTANT_MAP.fetch_categories
          );
          if (data?.success) {
            setCategories(data.categories || []);
          }
        } catch (error) {
          console.error("Error fetching categories:", error);
          // Fallback to constant if API fails
          setCategories(CATEGORIES);
        }
      };
      fetchCategories();
    }
  }, []);

  // Check for onboarding redirect
  useEffect(() => {
    if (userState?.user?.token && userState.user.onboarding_status === 'pending') {
      const currentPath = window.location.pathname + window.location.search;
      const onboardingUrl = `/onboarding?redirect=${encodeURIComponent(currentPath)}`;
      navigate(onboardingUrl);
    }
  }, [userState?.user?.onboarding_status, userState?.user?.token, navigate]);

  // Combined initial load and URL parameter handling to prevent double API calls
  useEffect(() => {
    // Wait for categories to be loaded before proceeding
    if (categories.length === 0) return;
    
    const hasUrlParams = initialZipcode || initialCategory || initialMinBudget || initialMaxBudget;
    
    if (hasUrlParams) {
      // URL parameters detected (email link) - apply filters immediately
      console.log("Auto-applying filters from URL params:", {
        zipcode: initialZipcode,
        category: initialCategory,
        minBudget: initialMinBudget,
        maxBudget: initialMaxBudget
      });
      
      applyFilters();
    } else {
      // No URL parameters - perform initial load with default payload
      const defaultPayload = initialPayload || {
        task_type: "all",
        sort_by: "date_desc",
      };
      
      console.log("Initial fetch with payload:", defaultPayload);
      fetchTasks(defaultPayload);
    }
  }, [categories]); // Depend on categories being loaded

  useEffect(() => {
    setActiveTab("qa");
    setShowOfferForm(false);
    setShowQuestionForm(false);
  }, [selectedTask?.id]);

  // Handle mobile browser back button navigation
  useEffect(() => {
    const handlePopState = () => {
      // Only handle when we have a selected task and we're on mobile
      const isMobile = window.innerWidth <= 1024;
      if (selectedTask && isMobile) {
        // Clear selected task to return to list view
        setSelectedTask(null);
      }
    };

    // Add event listener for browser back button
    window.addEventListener('popstate', handlePopState);

    // When a task is selected on mobile, add a history entry
    const isMobile = window.innerWidth <= 1024;
    if (selectedTask && isMobile) {
      window.history.pushState({ hasSelectedTask: true }, '');
    }

    // Cleanup
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [selectedTask]);

  const isMobile = typeof window !== "undefined" && window.innerWidth <= 1024;

  // Build and execute filter payload - only called explicitly
  const applyFilters = (overrides = {}) => {
    const currentTaskType =
      overrides.taskType !== undefined ? overrides.taskType : taskType;

    const payload = {
      q: (overrides.query !== undefined ? overrides.query : query).trim(),
      task_type: currentTaskType,
      // Don't include location data for remote tasks
      zipcode:
        currentTaskType === "online"
          ? ""
          : (overrides.zipcode !== undefined
              ? overrides.zipcode
              : zipcode
            ).trim(),
      radius:
        currentTaskType === "online"
          ? 25
          : overrides.radius !== undefined
          ? overrides.radius
          : radius,
      lat:
        currentTaskType === "online"
          ? null
          : overrides.userLocation !== undefined
          ? overrides.userLocation?.lat ?? null
          : userLocation?.lat ?? null,
      lng:
        currentTaskType === "online"
          ? null
          : overrides.userLocation !== undefined
          ? overrides.userLocation?.lng ?? null
          : userLocation?.lng ?? null,
      min_budget:
        overrides.minBudget !== undefined
          ? overrides.minBudget
            ? Number(overrides.minBudget)
            : null
          : minBudget
          ? Number(minBudget)
          : null,
      max_budget:
        overrides.maxBudget !== undefined
          ? overrides.maxBudget
            ? Number(overrides.maxBudget)
            : null
          : maxBudget
          ? Number(maxBudget)
          : null,
      sort_by: overrides.sortBy !== undefined ? overrides.sortBy : sortBy,
      category_id:
        overrides.selectedCategory !== undefined
          ? overrides.selectedCategory || null
          : selectedCategory || null,
    };
    console.log("🔍 ZIP Filter Debug - Payload being sent:", payload);
    console.log("🔍 ZIP Filter Debug - Current state:", {
      taskType: currentTaskType,
      zipcode,
      userLocation,
      radius,
    });
    fetchTasks(payload);
    if (isMobile) setIsFiltersOpen(false);
  };

  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (query.trim()) count++;
    if (taskType !== "all") count++;
    if (minBudget || maxBudget) count++;
    if (zipcode.trim() || userLocation) count++;
    if (selectedCategory) count++;
    if (sortBy !== "date_desc") count++;
    return count;
  }, [
    query,
    taskType,
    minBudget,
    maxBudget,
    zipcode,
    userLocation,
    selectedCategory,
    sortBy,
  ]);

  const [isGettingLocation, setIsGettingLocation] = useState(false);

  const getCurrentLocation = () => {
    // If location is already set, clear it (toggle off)
    if (userLocation) {
      setUserLocation(null);
      return;
    }

    if (!navigator?.geolocation) {
      console.warn("Geolocation is not supported by your browser.");
      return;
    }

    setIsGettingLocation(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const newLocation = {
          lat: Number(pos.coords.latitude.toFixed(6)),
          lng: Number(pos.coords.longitude.toFixed(6)),
        };
        setUserLocation(newLocation);
        setZipcode("");
        setIsGettingLocation(false);
        // Don't auto-apply filters - let user click Apply button
      },
      (err) => {
        console.warn("Geolocation error:", err);
        setIsGettingLocation(false);
      },
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 0 }
    );
  };

  const clearFilters = () => {
    setQuery("");
    setTaskType("all");
    setZipcode("");
    setRadius(25);
    setUserLocation(null);
    setMinBudget("");
    setMaxBudget("");
    setSelectedCategory("");
    setSortBy("date_desc");

    // Explicitly trigger search with cleared filter values
    applyFilters({
      query: "",
      taskType: "all",
      zipcode: "",
      radius: 25,
      userLocation: null,
      minBudget: "",
      maxBudget: "",
      selectedCategory: "",
      sortBy: "date_desc",
    });
  };

  // Report function - opens modal for type selection
  const handleReport = (entityType, entityId) => {
    if (!userState?.user?.token) {
      alert('Please sign in to report content');
      return;
    }

    setReportEntity({ type: entityType, id: entityId });
    setSelectedReportType(null);
    setIsReportModalOpen(true);
  };

  // Submit report function
  const submitReport = async () => {
    if (!selectedReportType) return;

    setIsSubmittingReport(true);

    try {
      const response = await request(
        "POST",
        API_CONSTANT_MAP.submit_report,
        {
          entity_type: reportEntity.type,
          entity_id: reportEntity.id,
          report_type: selectedReportType,
          description: `Reported ${reportEntity.type} as ${selectedReportType}`,
        }
      );

      if (response.data?.success) {
        alert('Thank you for your report. We will review it and take appropriate action.');
        setIsReportModalOpen(false);
        setReportEntity({ type: null, id: null });
        setSelectedReportType(null);
      } else {
        alert(response.data?.error?.message || 'Failed to submit report');
      }
    } catch (error) {
      console.error('Error submitting report:', error);
      alert('Failed to submit report. Please try again.');
    }

    setIsSubmittingReport(false);
  };

  // Computed values
  const hasActiveFilters = !!(
    query.trim() ||
    taskType !== "all" ||
    zipcode.trim() ||
    userLocation ||
    minBudget ||
    maxBudget ||
    selectedCategory ||
    sortBy !== "date_desc"
  );

  const getTaskTypeText = () =>
    taskType === "online"
      ? "Remote"
      : taskType === "in-person"
      ? "In-person"
      : "All Tasks";
  const getSortByText = () =>
    ({
      date_asc: "Oldest first",
      budget_desc: "Highest budget",
      budget_asc: "Lowest budget",
    }[sortBy] || "Most Recent");
  const getLocationText = () =>
    userLocation
      ? `My Location (${radius} mi)`
      : zipcode
      ? `${zipcode} (${radius} mi)`
      : "Anywhere";
  const getBudgetText = () =>
    minBudget && maxBudget
      ? `$${minBudget} - $${maxBudget}`
      : minBudget
      ? `Min $${minBudget}`
      : maxBudget
      ? `Max $${maxBudget}`
      : "Any Budget";

  const getCategoryText = () => {
    if (!selectedCategory) return "All Categories";
    const category = categories.find(
      (cat) => cat.id.toString() === selectedCategory.toString()
    );
    return category ? category.name : "All Categories";
  };

  const getCategoryName = (categoryId) => {
    if (!categoryId) return null;
    const category = CATEGORIES.find((cat) => cat.id === categoryId);
    return category ? category.name : null;
  };

  return (
    <>
      <GlobalStyle />
      <Page>
        <NavBar user={userState.user} />
        <Container>
          {/* Mobile Header - Search + Filter for mobile */}
          <MobileHeader
            style={{
              display: selectedTask ? "none" : undefined,
            }}
          >
            <MobileSearchContainer>
              <MobileSearchInput
                type="text"
                placeholder="Search for anything"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    applyFilters();
                  }
                }}
              />
              <MobileSearchButton onClick={() => applyFilters()}>
                <Search size={16} />
              </MobileSearchButton>
            </MobileSearchContainer>
            <MobileFilterIcon onClick={() => setIsFiltersOpen(true)}>
              <Filter size={16} />
              {activeFiltersCount > 0 && (
                <span className="filter-badge">{activeFiltersCount}</span>
              )}
            </MobileFilterIcon>
          </MobileHeader>

          {/* Desktop LinkedIn-style filters */}
          <SearchBarContainer
            style={{
              display: window.innerWidth <= 1024 ? "none" : "flex",
              marginBottom: "16px",
            }}
          >
            <SearchBar>
              <SearchIcon>
                <Search size={20} />
              </SearchIcon>
              <SearchInput
                type="text"
                placeholder="Search tasks..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    applyFilters();
                  }
                }}
              />
              <SearchButton
                onClick={(e) => {
                  e.preventDefault();
                  applyFilters();
                }}
                type="button"
              >
                <Search size={16} />
              </SearchButton>
            </SearchBar>

            <FilterPillsRow>
              {/* Location dropdown */}
              <FilterPill
                ref={locationDropdownRef}
                filled={zipcode || userLocation}
                disabled={taskType === "online"}
                onClick={() => {
                  if (taskType === "online") return;
                  setShowLocationDropdown(!showLocationDropdown);
                  setShowBudgetDropdown(false);
                }}
                style={{
                  opacity: taskType === "online" ? 0.5 : 1,
                  cursor: taskType === "online" ? "not-allowed" : "pointer",
                  pointerEvents: taskType === "online" ? "none" : "auto",
                }}
              >
                {getLocationText()}
                <ChevronDown size={14} />
                {showLocationDropdown && (
                  <DropdownMenu onClick={(e) => e.stopPropagation()}>
                    <DropdownContent>
                      <DropdownLabel>Search near a ZIP code</DropdownLabel>
                      <DropdownInput
                        type="text"
                        placeholder="Enter ZIP code"
                        value={zipcode}
                        onChange={(e) => {
                          setZipcode(e.target.value);
                          if (e.target.value.trim()) setUserLocation(null);
                        }}
                      />
                      <DropdownLabel style={{ marginTop: "16px" }}>
                        Or use your current location
                      </DropdownLabel>
                      <DropdownButton
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          getCurrentLocation();
                        }}
                        style={{
                          width: "100%",
                          marginBottom: "16px",
                          background: userLocation
                            ? "#059669"
                            : undefined,
                          color: userLocation ? "white" : undefined,
                        }}
                      >
                        <Navigation size={16} />{" "}
                        {isGettingLocation
                          ? "Getting Location..."
                          : userLocation
                          ? "Location Set ✓"
                          : "Use My Location"}
                      </DropdownButton>
                      {userLocation && (
                        <div
                          style={{
                            marginBottom: "16px",
                            padding: "10px 12px",
                            background: "#ffffff",
                            border: "1px solid #d1fae5",
                            borderRadius: "8px",
                            fontSize: "12px",
                            color: "#065f46",
                            fontWeight: "600",
                            textAlign: "center",
                          }}
                        >
                          📍 {userLocation.lat}, {userLocation.lng}
                        </div>
                      )}
                      <DropdownLabel>Search radius</DropdownLabel>
                      <MobileFilterSelect
                        value={radius}
                        onChange={(e) => setRadius(e.target.value)}
                      >
                        <option value="5">5 miles</option>
                        <option value="10">10 miles</option>
                        <option value="25">25 miles</option>
                        <option value="50">50 miles</option>
                        <option value="100">100 miles</option>
                      </MobileFilterSelect>
                      <DropdownActions>
                        <DropdownButton
                          onClick={(e) => {
                            e.preventDefault();
                            setZipcode("");
                            setUserLocation(null);
                            applyFilters({ zipcode: "", userLocation: null });
                            setShowLocationDropdown(false);
                          }}
                        >
                          Clear
                        </DropdownButton>
                        <DropdownButton
                          primary
                          onClick={(e) => {
                            e.preventDefault();
                            applyFilters();
                            setShowLocationDropdown(false);
                          }}
                        >
                          Apply
                        </DropdownButton>
                      </DropdownActions>
                    </DropdownContent>
                  </DropdownMenu>
                )}
              </FilterPill>

              {/* Budget Filter */}
              <FilterPill
                ref={budgetDropdownRef}
                filled={minBudget || maxBudget}
                onClick={() => {
                  setShowBudgetDropdown(!showBudgetDropdown);
                  setShowLocationDropdown(false);
                }}
              >
                {getBudgetText()}
                <ChevronDown size={14} />
                {showBudgetDropdown && (
                  <DropdownMenu onClick={(e) => e.stopPropagation()}>
                    <DropdownContent>
                      <DropdownLabel>Budget range</DropdownLabel>
                      <BudgetInputRow>
                        <DropdownInput
                          type="number"
                          placeholder="Min"
                          value={minBudget}
                          onChange={(e) => setMinBudget(e.target.value)}
                        />
                        <span>to</span>
                        <DropdownInput
                          type="number"
                          placeholder="Max"
                          value={maxBudget}
                          onChange={(e) => setMaxBudget(e.target.value)}
                        />
                      </BudgetInputRow>
                      <DropdownActions>
                        <DropdownButton
                          onClick={(e) => {
                            e.preventDefault();
                            setMinBudget("");
                            setMaxBudget("");
                            applyFilters({ minBudget: "", maxBudget: "" });
                            setShowBudgetDropdown(false);
                          }}
                        >
                          Clear
                        </DropdownButton>
                        <DropdownButton
                          primary
                          onClick={(e) => {
                            e.preventDefault();
                            applyFilters();
                            setShowBudgetDropdown(false);
                          }}
                        >
                          Apply
                        </DropdownButton>
                      </DropdownActions>
                    </DropdownContent>
                  </DropdownMenu>
                )}
              </FilterPill>

              {/* Category Filter */}
              <FilterPill
                ref={categoryDropdownRef}
                filled={selectedCategory}
                onClick={() => {
                  setShowCategoryDropdown(!showCategoryDropdown);
                  setShowBudgetDropdown(false);
                  setShowLocationDropdown(false);
                }}
              >
                {getCategoryText()}
                <ChevronDown size={14} />
                {showCategoryDropdown && (
                  <DropdownMenu onClick={(e) => e.stopPropagation()}>
                    <DropdownContent>
                      <DropdownLabel>Task Category</DropdownLabel>
                      <div style={{ maxHeight: "200px", overflowY: "auto" }}>
                        <DropdownOption
                          onClick={() => {
                            setSelectedCategory("");
                            applyFilters({ selectedCategory: "" });
                            setShowCategoryDropdown(false);
                          }}
                          style={{
                            background: !selectedCategory
                              ? "#f8fafc"
                              : "transparent",
                            fontWeight: !selectedCategory ? "600" : "400",
                          }}
                        >
                          All Categories
                        </DropdownOption>
                        {categories.map((category) => (
                          <DropdownOption
                            key={category.id}
                            onClick={() => {
                              setSelectedCategory(category.id.toString());
                              applyFilters({
                                selectedCategory: category.id.toString(),
                              });
                              setShowCategoryDropdown(false);
                            }}
                            style={{
                              background:
                                selectedCategory === category.id.toString()
                                  ? "#f8fafc"
                                  : "transparent",
                              fontWeight:
                                selectedCategory === category.id.toString()
                                  ? "600"
                                  : "400",
                            }}
                          >
                            {category.name}
                          </DropdownOption>
                        ))}
                      </div>
                      <DropdownActions>
                        <DropdownButton
                          onClick={(e) => {
                            e.preventDefault();
                            setSelectedCategory("");
                            applyFilters({ selectedCategory: "" });
                            setShowCategoryDropdown(false);
                          }}
                        >
                          Clear
                        </DropdownButton>
                      </DropdownActions>
                    </DropdownContent>
                  </DropdownMenu>
                )}
              </FilterPill>

              {/* Task Type Filter */}
              <FilterPill
                filled={taskType !== "all"}
                onClick={() => {
                  /* Not used with native select */
                }}
              >
                {getTaskTypeText()}
                <select
                  value={taskType}
                  onChange={(e) => {
                    const newTaskType = e.target.value;
                    setTaskType(newTaskType);
                    applyFilters({ taskType: newTaskType });
                  }}
                  style={{
                    opacity: 0,
                    position: "absolute",
                    width: "100%",
                    height: "100%",
                    cursor: "pointer",
                  }}
                >
                  <option value="all">All tasks</option>
                  <option value="online">Remote</option>
                  <option value="in-person">In-person</option>
                </select>
                <ChevronDown size={14} />
              </FilterPill>

              {/* Sort By Filter */}
              <FilterPill
                filled={sortBy !== "date_desc"}
                onClick={() => {
                  /* Not used with native select */
                }}
              >
                {getSortByText()}
                <select
                  value={sortBy}
                  onChange={(e) => {
                    const newSortBy = e.target.value;
                    setSortBy(newSortBy);
                    applyFilters({ sortBy: newSortBy });
                  }}
                  style={{
                    opacity: 0,
                    position: "absolute",
                    width: "100%",
                    height: "100%",
                    cursor: "pointer",
                  }}
                >
                  <option value="date_desc">Most recent</option>
                  <option value="date_asc">Oldest first</option>
                  <option value="budget_desc">Highest budget</option>
                  <option value="budget_asc">Lowest budget</option>
                </select>
                <ChevronDown size={14} />
              </FilterPill>

              {/* Clear Filters */}
              {hasActiveFilters && (
                <FilterPill
                  onClick={(e) => {
                    e.preventDefault();
                    clearFilters();
                  }}
                  danger
                >
                  <X size={14} style={{ marginRight: "4px" }} />
                  Clear
                </FilterPill>
              )}
            </FilterPillsRow>
          </SearchBarContainer>

          {/* Mobile bottom sheet */}
          {isMobile && isFiltersOpen && (
            <>
              <Backdrop onClick={() => setIsFiltersOpen(false)} />
              <MobileSheet ref={mobileSheetRef}>
                <MobileSheetHeader>
                  <h2>
                    <Filter size={20} /> Filter Tasks
                  </h2>
                  <button
                    className="close-button"
                    onClick={() => setIsFiltersOpen(false)}
                  >
                    <X size={20} />
                  </button>
                </MobileSheetHeader>

                <MobileSheetContent>
                  <MobileFilterForm onSubmit={(e) => e.preventDefault()}>
                    <MobileFilterGroup hasValue={query.trim()}>
                      <MobileFilterLabel className="filter-label">
                        Search by title or description
                      </MobileFilterLabel>
                      <MobileFilterInput
                        type="text"
                        placeholder="Search tasks..."
                        value={query}
                        hasValue={query.trim()}
                        onChange={(e) => setQuery(e.target.value)}
                      />
                    </MobileFilterGroup>

                    <MobileFilterGroup hasValue={taskType !== "all"}>
                      <MobileFilterLabel className="filter-label">Task Type</MobileFilterLabel>
                      <MobileFilterSelect
                        value={taskType}
                        hasValue={taskType !== "all"}
                        onChange={(e) => {
                          const newTaskType = e.target.value;
                          setTaskType(newTaskType);
                        }}
                      >
                        <option value="all">All</option>
                        <option value="online">Remote</option>
                        <option value="in-person">In-person</option>
                      </MobileFilterSelect>
                    </MobileFilterGroup>

                    <MobileFilterGroup hasValue={selectedCategory}>
                      <MobileFilterLabel className="filter-label">Category</MobileFilterLabel>
                      <MobileFilterSelect
                        value={selectedCategory}
                        hasValue={selectedCategory}
                        onChange={(e) => {
                          const newCategory = e.target.value;
                          setSelectedCategory(newCategory);
                        }}
                      >
                        <option value="">All Categories</option>
                        {categories.map((category) => (
                          <option
                            key={category.id}
                            value={category.id.toString()}
                          >
                            {category.name}
                          </option>
                        ))}
                      </MobileFilterSelect>
                    </MobileFilterGroup>

                    <MobileFilterGroup hasValue={sortBy !== "date_desc"}>
                      <MobileFilterLabel className="filter-label">Sort By</MobileFilterLabel>
                      <MobileFilterSelect
                        value={sortBy}
                        hasValue={sortBy !== "date_desc"}
                        onChange={(e) => {
                          const newSortBy = e.target.value;
                          setSortBy(newSortBy);
                        }}
                      >
                        <option value="date_desc">Newest</option>
                        <option value="date_asc">Oldest</option>
                        <option value="budget_desc">Budget: High to Low</option>
                        <option value="budget_asc">Budget: Low to High</option>
                      </MobileFilterSelect>
                    </MobileFilterGroup>

                    <MobileFilterGroup hasValue={minBudget || maxBudget}>
                      <MobileFilterLabel className="filter-label">Budget ($)</MobileFilterLabel>
                      <BudgetInputRow style={{ gap: "8px" }}>
                        <MobileFilterInput
                          type="number"
                          inputMode="numeric"
                          placeholder="Min"
                          value={minBudget}
                          hasValue={minBudget}
                          onChange={(e) => setMinBudget(e.target.value)}
                        />
                        <span>to</span>
                        <MobileFilterInput
                          type="number"
                          inputMode="numeric"
                          placeholder="Max"
                          value={maxBudget}
                          hasValue={maxBudget}
                          onChange={(e) => setMaxBudget(e.target.value)}
                        />
                      </BudgetInputRow>
                    </MobileFilterGroup>

                    <MobileFilterGroup
                      hasValue={zipcode && taskType !== "online"}
                      style={{
                        opacity: taskType === "online" ? 0.5 : 1,
                        pointerEvents: taskType === "online" ? "none" : "auto",
                      }}
                    >
                      <MobileFilterLabel className="filter-label">
                        Location{" "}
                        {taskType === "online"
                          ? "(disabled for remote tasks)"
                          : ""}
                      </MobileFilterLabel>
                      <MobileFilterInput
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        maxLength="10"
                        placeholder={
                          taskType === "online"
                            ? "Not applicable for remote tasks"
                            : "Enter ZIP code"
                        }
                        value={zipcode}
                        hasValue={zipcode && taskType !== "online"}
                        disabled={taskType === "online"}
                        onChange={(e) => {
                          if (taskType === "online") return;
                          setZipcode(e.target.value);
                          if (e.target.value.trim()) setUserLocation(null);
                        }}
                      />
                      <MobileButton
                        type="button"
                        onClick={
                          taskType === "online" ? undefined : getCurrentLocation
                        }
                        disabled={taskType === "online" || isGettingLocation}
                        style={{
                          marginTop: "8px",
                          cursor:
                            taskType === "online" || isGettingLocation
                              ? "not-allowed"
                              : "pointer",
                          background: userLocation
                            ? "#059669"
                            : undefined,
                        }}
                      >
                        <Navigation size={16} />{" "}
                        {isGettingLocation
                          ? "Getting Location..."
                          : userLocation
                          ? "Location Set ✓"
                          : "Use My Location"}
                      </MobileButton>
                      {userLocation && (
                        <div
                          style={{
                            marginTop: "8px",
                            padding: "12px 16px",
                            background: "#ffffff",
                            border: "1px solid #d1fae5",
                            borderRadius: "10px",
                            fontSize: "13px",
                            color: "#065f46",
                            fontWeight: "600",
                          }}
                        >
                          📍 Using your location: {userLocation.lat},{" "}
                          {userLocation.lng}
                        </div>
                      )}
                    </MobileFilterGroup>

                    <MobileFilterGroup
                      hasValue={radius !== 25 && taskType !== "online"}
                      style={{
                        opacity: taskType === "online" ? 0.5 : 1,
                        pointerEvents: taskType === "online" ? "none" : "auto",
                      }}
                    >
                      <MobileFilterLabel className="filter-label">
                        Search Radius ({radius} miles){" "}
                        {taskType === "online"
                          ? "(disabled for remote tasks)"
                          : ""}
                      </MobileFilterLabel>
                      <input
                        type="range"
                        min="5"
                        max="100"
                        step="5"
                        value={radius}
                        disabled={taskType === "online"}
                        onChange={(e) => {
                          if (taskType === "online") return;
                          setRadius(Number(e.target.value));
                        }}
                        style={{ width: "100%" }}
                      />
                    </MobileFilterGroup>
                  </MobileFilterForm>
                </MobileSheetContent>

                <MobileSheetFooter>
                  <MobileButton onClick={clearFilters} style={{ flex: 1 }}>
                    Clear
                  </MobileButton>
                  <MobileButton
                    primary
                    onClick={applyFilters}
                    style={{ flex: 1 }}
                  >
                    Apply Filters
                  </MobileButton>
                </MobileSheetFooter>
              </MobileSheet>
            </>
          )}

          <Grid>
            {/* Sidebar list */}
            <Sidebar selectedTask={selectedTask}>
              <ListHeader>
                <div
                  style={{ fontWeight: 700, color: "#1e293b", fontSize: "0.9375rem" }}
                >
                  {isLoading ? "Loading tasks..." : `${tasks.length} ${tasks.length === 1 ? 'task' : 'tasks'}`}
                </div>
              </ListHeader>

              <List>
                {isLoading ? (
                  Array.from({ length: 4 }).map((_, i) => (
                    <div
                      key={i}
                      style={{
                        padding: "16px",
                        borderRadius: 12,
                        border: `1px solid ${theme.colors.border}`,
                        background: "#fff",
                        marginBottom: "8px",
                      }}
                    >
                      <Skeleton h="18px" style={{ marginBottom: "8px" }} />
                      <Skeleton h="14px" style={{ width: "70%" }} />
                    </div>
                  ))
                ) : tasks.length === 0 ? (
                  <Empty>
                    <div className="content">
                      <p className="title">
                        {query.trim() 
                          ? `No "${query.trim()}" tasks match your filters.`
                          : "No tasks match your filters."
                        }
                      </p>
                      <p className="description">
                        Try adjusting your filters to expand results and find more
                        opportunities.
                      </p>
                    </div>
                    <EmptyStateButton
                      onClick={() =>
                        isMobile ? setIsFiltersOpen(true) : clearFilters()
                      }
                    >
                      <Filter size={16} />
                      {isMobile ? "Adjust Filters" : "Clear Filters"}
                    </EmptyStateButton>
                    {userState?.user?.user_id ? (
                      // Signed in users - offer to set up detailed notification preferences
                      <NotificationBanner style={{ marginTop: "16px" }}>
                        <div className="content">
                          <p className="title">
                            {query.trim() 
                              ? `Get notified when "${query.trim()}" tasks are posted`
                              : "Get notified when tasks are posted"
                            }
                          </p>
                          <p className="description">
                            {query.trim()
                              ? `Set up custom alerts for "${query.trim()}" tasks matching your criteria.`
                              : "Set up custom alerts for tasks matching your criteria."
                            }
                          </p>
                        </div>
                        <NotificationButton
                          onClick={() => navigate("/account?tab=preferences")}
                        >
                          {query.trim() 
                            ? `Set Alerts for "${query.trim()}"`
                            : "Set Alerts"
                          }
                        </NotificationButton>
                      </NotificationBanner>
                    ) : (
                      // Not signed in - encourage sign up for notifications
                      <>
                        <div
                          style={{
                            fontSize: "0.9rem",
                            marginTop: "1rem",
                            color: theme.colors.textBody,
                          }}
                        >
                          Sign up to get notified when new tasks are posted in
                          your area!
                        </div>
                        <EmptyStateButton onClick={() => navigate("/signup")}>
                          <User size={16} />
                          Sign Up for Notifications
                        </EmptyStateButton>
                      </>
                    )}
                  </Empty>
                ) : (
                  tasks.map((task) => (
                    <Item
                      key={task.id}
                      selected={selectedTask?.id === task.id}
                      onClick={() => setSelectedTask(task)}
                      onMouseEnter={() => prefetchOffers(task.id)}
                      onMouseLeave={() => cancelPrefetch(task.id)}
                    >
                      <ItemTopRow>
                        <ItemHeader>
                          <ItemTitle 
                            style={{ textTransform: "capitalize" }}
                            title={task.title}
                          >
                            {task.title}
                          </ItemTitle>
                          <ItemSubtitle>
                            <Avatar
                              src={task.poster?.avatar}
                              firstName={task.poster?.first_name}
                              lastName={task.poster?.last_name}
                              userId={task.poster?.id || task.creator_id}
                              size="28px"
                              style={{
                                marginRight: "8px",
                                verticalAlign: "middle",
                              }}
                            />
                            {(task.poster?.first_name || "Anonymous").toLowerCase().replace(/\b\w/g, l => l.toUpperCase())}
                          </ItemSubtitle>
                        </ItemHeader>
                        <ItemRightSection>
                          <Price>${task.budget}{task.budget_type === 'hourly' ? '/hr' : ''}</Price>
                        </ItemRightSection>
                      </ItemTopRow>

                      <ItemRow>
                        {getCategoryName(task.category_id) && (
                          <Badge style={{ 
                            background: theme.colors.primaryLight, 
                            color: theme.colors.primary,
                            fontWeight: '600'
                          }}>
                            <Briefcase size={14} />
                            {getCategoryName(task.category_id)}
                          </Badge>
                        )}
                        <Badge>
                          <MapPin size={14} />
                          {task.task_type === "online"
                            ? "Remote"
                            : task.task_type === "in-person"
                            ? task.location || "In-person"
                            : task.location || "Location not specified"}
                        </Badge>
                        <Badge>
                          <Clock size={14} />
                          {formatDistanceToNow(parseDate(task.created_at), {
                            addSuffix: true,
                          })}
                        </Badge>
                        {typeof task.distance_miles === "number" && (
                          <Badge>
                            <Navigation size={14} />
                            {task.distance_miles.toFixed(1)} mi
                          </Badge>
                        )}
                      </ItemRow>
                    </Item>
                  ))
                )}
              </List>
            </Sidebar>

            {/* Details */}
            {selectedTask ? (
              <Details ref={detailsPanelRef} selectedTask={selectedTask}>
                <StickyHead>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {/* Back button - only visible in Capacitor app on mobile */}
                    <button
                      onClick={() => setSelectedTask(null)}
                      aria-label="Back to task list"
                      style={{
                        display: 'none',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '4px',
                        marginLeft: '-4px',
                        color: '#0f172a',
                      }}
                      className="capacitor-back-button"
                    >
                      <ChevronLeft size={28} strokeWidth={2.5} />
                    </button>
                    <Link
                      to={`/tasks/${selectedTask.id}`}
                      style={{ textTransform: "capitalize" }}
                    >
                      {selectedTask.title}
                    </Link>
                  </div>
                  <PosterRow>
                    <PosterInfo>
                      <span>
                        Posted{" "}
                        {formatDistanceToNow(
                          parseDate(selectedTask.created_at),
                          {
                            addSuffix: true,
                          }
                        )}{" "}
                        by
                      </span>
                      <Avatar
                        src={selectedTask.poster?.avatar}
                        firstName={selectedTask.poster?.first_name}
                        lastName={selectedTask.poster?.last_name}
                        userId={selectedTask.poster?.id || selectedTask.creator_id}
                        size="32px"
                      />
                      <span
                        style={{
                          color: theme.colors.textBody,
                          textDecoration: "none",
                          transition: "text-decoration 0.2s ease",
                          fontWeight: 700,
                          cursor: "pointer",
                          textTransform: "capitalize",
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.textDecoration = "underline";
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.textDecoration = "none";
                        }}
                        onClick={() => {
                          window.location.href = `/users/${selectedTask.poster?.id}`;
                        }}
                      >
                        {selectedTask.poster?.first_name &&
                        selectedTask.poster?.last_name
                          ? `${selectedTask.poster.first_name} ${selectedTask.poster.last_name}`.toLowerCase().replace(/\b\w/g, l => l.toUpperCase())
                          : (selectedTask.poster?.username || "User").toLowerCase().replace(/\b\w/g, l => l.toUpperCase())}
                      </span>
                    </PosterInfo>

                    {/* Show Manage Task button if user is the poster */}
                    {userState.user &&
                      selectedTask.poster?.id === userState.user.user_id && (
                        <ManageTaskButton
                          onClick={() =>
                            navigate(`/tasks/${selectedTask.id}/manage`)
                          }
                        >
                          <Settings size={14} /> Manage Task
                        </ManageTaskButton>
                      )}
                  </PosterRow>
                </StickyHead>

                <ContentArea>
                  <StatGrid>
                    <SoftStat>
                      <div>
                        <DollarSign size={18} />
                      </div>
                      <div>
                        <div className="label">Budget</div>
                        <div className="value">${selectedTask.budget}{selectedTask.budget_type === 'hourly' ? '/hr' : ''}</div>
                      </div>
                    </SoftStat>
                    <SoftStat>
                      <div>
                        <Calendar size={18} />
                      </div>
                      <div>
                        <div className="label">Deadline</div>
                        <div className="value">
                          {selectedTask.due_date
                            ? format(
                                parseDate(selectedTask.due_date),
                                "MMM d, yyyy"
                              )
                            : "N/A"}
                        </div>
                      </div>
                    </SoftStat>
                    <SoftStat>
                      <div>
                        <MapPin size={18} />
                      </div>
                      <div>
                        <div className="label">Location</div>
                        <div className="value">
                          {selectedTask.task_type === "online"
                            ? "Remote"
                            : selectedTask.task_type === "in-person"
                            ? selectedTask.location || "In-person"
                            : selectedTask.location || "Location not specified"}
                        </div>
                      </div>
                    </SoftStat>
                  </StatGrid>

                  <ButtonRow>
                    {/* Show user's existing offer inline, or Submit an Offer button */}
                    {userOffer && !showOfferForm && selectedTask?.poster?.id !== userState?.user?.user_id ? (
                      <UserOfferCard>
                        <UserOfferTitle>
                          <Check size={14} /> Your Offer:
                        </UserOfferTitle>
                        <UserOfferAmount>${userOffer.offer_amount}</UserOfferAmount>
                        <EditOfferButton
                          onClick={() => {
                            setShowOfferForm(true);
                            setShowQuestionForm(false);
                            setActiveTab("offers");
                            setTimeout(() => {
                              document.getElementById('offer-form-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                            }, 100);
                          }}
                        >
                          <Pencil size={12} /> Edit
                        </EditOfferButton>
                      </UserOfferCard>
                    ) : !userOffer && selectedTask?.poster?.id !== userState?.user?.user_id ? (
                      <Primary
                        onClick={() => {
                          if (!userState?.user?.user_id) {
                            navigate("/signin");
                            return;
                          }
                          setShowOfferForm(true);
                          setActiveTab("offers");
                        }}
                      >
                        <Star size={16} /> Submit an Offer
                      </Primary>
                    ) : null}
                    <Ghost
                      onClick={() => {
                        if (!userState?.user?.user_id) {
                          navigate("/signin");
                          return;
                        }
                        setShowQuestionForm(true);
                        setActiveTab("qa");
                        setShowOfferForm(false);
                      }}
                    >
                      <MessageCircle size={16} /> Ask a Question
                    </Ghost>
                  </ButtonRow>

                  {showOfferForm && (
                    <div id="offer-form-section" style={{ marginTop: 14 }}>
                      <OfferFormComponent
                        task={selectedTask}
                        existingOffer={userOffer}
                        onCancel={() => setShowOfferForm(false)}
                        onOfferSubmitted={(o) => {
                          addNewOffer(o);
                          setShowOfferForm(false);
                        }}
                      />
                    </div>
                  )}

                  {showQuestionForm && (
                    <div style={{ marginTop: 14 }}>
                      <QuestionFormComponent
                        task={selectedTask}
                        onCancel={() => setShowQuestionForm(false)}
                        onQuestionSubmitted={(q) => {
                          addNewQuestion(q);
                          setShowQuestionForm(false);
                        }}
                      />
                    </div>
                  )}

                  {/* Task Report Button */}
                  {userState?.user?.token && selectedTask?.poster?.id !== userState?.user?.user_id && (
                    <div style={{ marginBottom: "24px", display: "flex", justifyContent: "flex-end" }}>
                      <ReportButton onClick={() => handleReport('task', selectedTask.id)}>
                        <Flag />
                        Report Task
                      </ReportButton>
                    </div>
                  )}

                  <Section>
                    <SectionTitle>
                      <Briefcase size={18} /> Project Description
                    </SectionTitle>
                    <Body>{linkifyText(selectedTask.description)}</Body>
                  </Section>

                  {selectedTask.time_windows?.length > 0 && (
                    <Section>
                      <SectionTitle>
                        <Clock size={18} /> Availability
                      </SectionTitle>
                      <TagWrap>
                        {selectedTask.time_windows.map((tw) => (
                          <TimePill key={tw.id}>
                            {tw.name}: {tw.start_time} – {tw.end_time}
                          </TimePill>
                        ))}
                      </TagWrap>
                    </Section>
                  )}

                  {selectedTask.images?.length > 0 && (
                    <Section>
                      <SectionTitle>
                        <ImageIcon size={18} /> Attachments
                      </SectionTitle>
                      <div
                        style={{ display: "flex", gap: 12, flexWrap: "wrap" }}
                      >
                        {selectedTask.images.map((img, index) => (
                          <Thumb
                            key={img.id}
                            src={img.url}
                            alt="Task Attachment"
                            onClick={() =>
                              openImageModal(selectedTask.images, index)
                            }
                          />
                        ))}
                      </div>
                    </Section>
                  )}

                  <Activity
                    activeTab={activeTab}
                    setActiveTab={setActiveTab}
                    offers={offers}
                    questions={questions}
                    isOffersLoading={isOffersLoading}
                    isQuestionsLoading={isQuestionsLoading}
                    selectedTask={selectedTask}
                    userState={userState}
                    replyingTo={replyingTo}
                    setReplyingTo={setReplyingTo}
                    replyText={replyText}
                    setReplyText={setReplyText}
                    replySubmitting={replySubmitting}
                    handleReplySubmit={handleReplySubmit}
                    handleReport={handleReport}
                    editingQuestion={editingQuestion}
                    editQuestionText={editQuestionText}
                    setEditQuestionText={setEditQuestionText}
                    editSubmitting={editSubmitting}
                    handleEditQuestion={handleEditQuestion}
                    handleEditSubmit={handleEditSubmit}
                    handleCancelEdit={handleCancelEdit}
                    editingReply={editingReply}
                    editReplyText={editReplyText}
                    setEditReplyText={setEditReplyText}
                    editReplySubmitting={editReplySubmitting}
                    handleEditReply={handleEditReply}
                    handleEditReplySubmit={handleEditReplySubmit}
                    handleCancelEditReply={handleCancelEditReply}
                    offerQuestionsById={offerQuestionsById}
                    offerQuestionsLoadingById={offerQuestionsLoadingById}
                    offerReplyingToById={offerReplyingToById}
                    offerReplyTextById={offerReplyTextById}
                    offerQuestionTextById={offerQuestionTextById}
                    offerQuestionSubmittingById={offerQuestionSubmittingById}
                    offerReplySubmittingById={offerReplySubmittingById}
                    offerChatExpandedById={offerChatExpandedById}
                    offerMenuOpenById={offerMenuOpenById}
                    setOfferReplyTextById={setOfferReplyTextById}
                    setOfferReplyingToById={setOfferReplyingToById}
                    setOfferQuestionTextById={setOfferQuestionTextById}
                    setOfferChatExpandedById={setOfferChatExpandedById}
                    setOfferMenuOpenById={setOfferMenuOpenById}
                    handleOfferQuestionSubmit={handleOfferQuestionSubmit}
                    handleOfferReplySubmit={handleOfferReplySubmit}
                    onEditOffer={() => {
                      setShowOfferForm(true);
                      setShowQuestionForm(false);
                      setTimeout(() => {
                        document.getElementById('offer-form-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }, 100);
                    }}
                  />
                </ContentArea>
              </Details>
            ) : isLoading ? (
              <Details>
                <div style={{ 
                  padding: "40px 20px", 
                  display: "flex", 
                  flexDirection: "column", 
                  alignItems: "center", 
                  justifyContent: "center", 
                  height: "400px",
                  gap: "16px"
                }}>
                  <Skeleton h="24px" style={{ width: "200px" }} />
                  <Skeleton h="16px" style={{ width: "150px" }} />
                  <div style={{ display: "flex", gap: "12px", marginTop: "20px" }}>
                    <Skeleton h="32px" style={{ width: "100px", borderRadius: "20px" }} />
                    <Skeleton h="32px" style={{ width: "80px", borderRadius: "20px" }} />
                  </div>
                  <div style={{ width: "100%", maxWidth: "300px" }}>
                    <Skeleton h="80px" style={{ width: "100%" }} />
                  </div>
                </div>
              </Details>
            ) : (
              <Details>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "400px",
                    textAlign: "center",
                    color: theme.colors.textMuted,
                    padding: "40px 20px",
                  }}
                >
                  <div style={{ marginBottom: "16px", opacity: 0.5 }}>
                    <Target size={48} />
                  </div>
                  <h3
                    style={{
                      margin: "0 0 8px 0",
                      color: theme.colors.textHeading,
                      fontSize: "1.1rem",
                      fontWeight: 600,
                    }}
                  >
                    Select a task to view details
                  </h3>
                  <p
                    style={{
                      margin: "0",
                      fontSize: "0.9rem",
                      maxWidth: "300px",
                      lineHeight: 1.5,
                    }}
                  >
                    Click on a task from the list to see its details,
                    requirements, and submit an offer.
                  </p>
                </div>
              </Details>
            )}
          </Grid>
        </Container>

        {/* Image Modal */}
        <ImageModal
          images={modalImages}
          currentIndex={currentImageIndex}
          isOpen={isImageModalOpen}
          onClose={closeImageModal}
          onNext={handleNextImage}
          onPrevious={handlePreviousImage}
        />

        {/* Report Modal */}
        {isReportModalOpen && (
          <ReportModal onClick={() => setIsReportModalOpen(false)}>
            <ReportModalContent onClick={(e) => e.stopPropagation()}>
              <ReportModalTitle>Report Content</ReportModalTitle>
              <p style={{ margin: '0 0 16px', color: '#6b7280', fontSize: '0.875rem' }}>
                Please select the reason for reporting this content:
              </p>
              
              <div>
                <ReportTypeOption 
                  onClick={() => setSelectedReportType('spam')}
                  style={{ 
                    borderColor: selectedReportType === 'spam' ? '#3b82f6' : '#e5e7eb',
                    background: selectedReportType === 'spam' ? '#eff6ff' : 'white'
                  }}
                >
                  <ReportOptionTitle>Spam</ReportOptionTitle>
                  <ReportOptionDescription>
                    Unwanted commercial content or repetitive posting
                  </ReportOptionDescription>
                </ReportTypeOption>

                <ReportTypeOption 
                  onClick={() => setSelectedReportType('fraud')}
                  style={{ 
                    borderColor: selectedReportType === 'fraud' ? '#3b82f6' : '#e5e7eb',
                    background: selectedReportType === 'fraud' ? '#eff6ff' : 'white'
                  }}
                >
                  <ReportOptionTitle>Fraud</ReportOptionTitle>
                  <ReportOptionDescription>
                    Misleading information or scam content
                  </ReportOptionDescription>
                </ReportTypeOption>

                <ReportTypeOption 
                  onClick={() => setSelectedReportType('inappropriate')}
                  style={{ 
                    borderColor: selectedReportType === 'inappropriate' ? '#3b82f6' : '#e5e7eb',
                    background: selectedReportType === 'inappropriate' ? '#eff6ff' : 'white'
                  }}
                >
                  <ReportOptionTitle>Inappropriate</ReportOptionTitle>
                  <ReportOptionDescription>
                    Offensive, abusive, or inappropriate content
                  </ReportOptionDescription>
                </ReportTypeOption>

                <ReportTypeOption 
                  onClick={() => setSelectedReportType('other')}
                  style={{ 
                    borderColor: selectedReportType === 'other' ? '#3b82f6' : '#e5e7eb',
                    background: selectedReportType === 'other' ? '#eff6ff' : 'white'
                  }}
                >
                  <ReportOptionTitle>Other</ReportOptionTitle>
                  <ReportOptionDescription>
                    Something else that violates our community guidelines
                  </ReportOptionDescription>
                </ReportTypeOption>
              </div>

              <ReportModalButtons>
                <ReportModalButton 
                  className="cancel" 
                  onClick={() => setIsReportModalOpen(false)}
                >
                  Cancel
                </ReportModalButton>
                <ReportModalButton 
                  className="submit" 
                  onClick={submitReport}
                  disabled={!selectedReportType || isSubmittingReport}
                >
                  {isSubmittingReport ? 'Submitting...' : 'Submit Report'}
                </ReportModalButton>
              </ReportModalButtons>
            </ReportModalContent>
          </ReportModal>
        )}
      </Page>
    </>
  );
};

const TabContainer = styled.div`
  display: flex;
  gap: 6px;
  margin: 0 0 20px;
  background: #ffffff;
  border-radius: 10px;
  padding: 4px;
  border: 1px solid ${theme.colors.border};
  box-shadow: none;
  width: fit-content;
  max-width: 400px;
`;

const TabButton = styled.button`
  background: ${(props) => (props.$active ? theme.colors.primaryLight : "transparent")};
  border: 1px solid ${(props) => (props.$active ? theme.colors.primaryLight : "transparent")};
  border-radius: 8px;
  padding: 6px 10px;
  font-weight: 600;
  font-size: 0.85rem;
  color: ${(props) =>
    props.$active ? theme.colors.primary : theme.colors.textMuted};
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease, border-color 0.2s ease;
  box-shadow: none;
  white-space: nowrap;

  &:hover {
    color: ${theme.colors.primary};
    background: ${theme.colors.primaryLight};
  }
`;

// Activity
const Activity = ({
  activeTab,
  setActiveTab,
  offers,
  questions = [],
  isOffersLoading,
  isQuestionsLoading,
  selectedTask,
  userState,
  replyingTo,
  setReplyingTo,
  replyText,
  setReplyText,
  replySubmitting,
  handleReplySubmit,
  handleReport,
  editingQuestion,
  editQuestionText,
  setEditQuestionText,
  editSubmitting,
  handleEditQuestion,
  handleEditSubmit,
  handleCancelEdit,
  editingReply,
  editReplyText,
  setEditReplyText,
  editReplySubmitting,
  handleEditReply,
  handleEditReplySubmit,
  handleCancelEditReply,
  offerQuestionsById,
  offerQuestionsLoadingById,
  offerReplyingToById,
  offerReplyTextById,
  offerQuestionTextById,
  offerQuestionSubmittingById,
  offerReplySubmittingById,
  offerChatExpandedById,
  offerMenuOpenById,
  setOfferReplyTextById,
  setOfferReplyingToById,
  setOfferQuestionTextById,
  setOfferChatExpandedById,
  setOfferMenuOpenById,
  handleOfferQuestionSubmit,
  handleOfferReplySubmit,
  onEditOffer,
}) => {
  return (
    <Section id="activity">
      <TabContainer>
        <TabButton
          $active={activeTab === "offers"}
          onClick={() => setActiveTab("offers")}
        >
          Offers ({offers.length})
        </TabButton>
        <TabButton
          $active={activeTab === "qa"}
          onClick={() => setActiveTab("qa")}
        >
          Q & A ({questions?.length || 0})
        </TabButton>
      </TabContainer>

      {activeTab === "offers" ? (
        <>
          {selectedTask.status && offers.length > 0 && (selectedTask.status === 'open' || selectedTask.status === 'in_progress') && (
            <div style={{ marginBottom: '16px' }}>
              <TaskProgressPill status={selectedTask.status}>
                {selectedTask.status === 'open' ? 'Still accepting offers' :
                 selectedTask.status === 'in_progress' ? 'Offer accepted' : 
                 selectedTask.status}
              </TaskProgressPill>
            </div>
          )}
  {offers.length ? (
  offers.map((offer, i) => {
    const offerQuestions = offerQuestionsById[offer.id] || [];
    const isOfferQuestionsLoading = !!offerQuestionsLoadingById[offer.id];
            const canPostOfferChat =
              !!userState?.user?.user_id &&
              (userState.user.user_id === offer.user?.id ||
                userState.user.user_id === selectedTask?.poster?.id);
    const isOfferChatExpanded = !!offerChatExpandedById[offer.id];
            const isOfferMenuOpen = !!offerMenuOpenById[offer.id];
            const canEditOffer = offer.user?.id === userState?.user?.user_id && onEditOffer;
            const canReportOffer = userState?.user?.token && offer.user?.id !== userState?.user?.user_id;
            const offerReplyingTo = offerReplyingToById[offer.id];
            const offerReplyText = offerReplyTextById[offer.id] || "";
            const offerQuestionText = offerQuestionTextById[offer.id] || "";
            const isOfferSubmitting = !!offerQuestionSubmittingById[offer.id];
            const isOfferReplySubmitting = !!offerReplySubmittingById[offer.id];
    return (
    <OfferCard key={offer.id} style={{ animationDelay: `${i * 80}ms` }}>
              <OfferHeader>
                <OfferHeaderMain>
                  <Avatar
                    src={offer.user?.avatar}
                    firstName={offer.user?.first_name}
                    lastName={offer.user?.last_name}
                    name={offer.user?.username}
                    userId={offer.user?.id}
                    size="32px"
                  />
                  <OfferUserInfo>
                    <OfferUserName>
                      {offer.user?.id ? (
                        <Link to={`/users/${offer.user.id}`}>
                          {offer.user?.first_name && offer.user?.last_name
                            ? `${offer.user.first_name} ${offer.user.last_name}`.trim().toLowerCase().replace(/\b\w/g, l => l.toUpperCase())
                            : (offer.user?.username || "Anonymous User").toLowerCase().replace(/\b\w/g, l => l.toUpperCase())}
                        </Link>
                      ) : (
                        <span>
                          {offer.user?.first_name && offer.user?.last_name
                            ? `${offer.user.first_name} ${offer.user.last_name}`.trim().toLowerCase().replace(/\b\w/g, l => l.toUpperCase())
                            : (offer.user?.username || "Anonymous User").toLowerCase().replace(/\b\w/g, l => l.toUpperCase())}
                        </span>
                      )}
                      <UserRating
                        userId={offer.user?.id}
                        averageRating={offer.user?.average_rating}
                        totalReviews={offer.user?.total_reviews}
                      />
                    </OfferUserName>
                    <OfferTimestamp>
                      {formatDistanceToNow(parseDate(offer.created_at), {
                        addSuffix: true,
                      })}
                    </OfferTimestamp>
                  </OfferUserInfo>
                </OfferHeaderMain>
                <OfferHeaderSide>
                  <OfferHeaderTopRow>
                    <OfferPrice>${offer.offer_amount}</OfferPrice>
                    {(canEditOffer || canReportOffer) && (
                      <OfferMenu>
                        <OfferMenuButton
                          onClick={() =>
                            setOfferMenuOpenById((p) => ({
                              ...p,
                              [offer.id]: !isOfferMenuOpen,
                            }))
                          }
                          aria-label="Offer actions"
                        >
                          <MoreHorizontal size={16} />
                        </OfferMenuButton>
                        {isOfferMenuOpen && (
                          <OfferMenuDropdown>
                            {canEditOffer && (
                              <OfferMenuItem
                                onClick={() => {
                                  setOfferMenuOpenById((p) => ({ ...p, [offer.id]: false }));
                                  onEditOffer();
                                }}
                              >
                                <Pencil size={14} /> Edit offer
                              </OfferMenuItem>
                            )}
                            {canReportOffer && (
                              <OfferMenuItem
                                onClick={() => {
                                  setOfferMenuOpenById((p) => ({ ...p, [offer.id]: false }));
                                  handleReport('offer', offer.id);
                                }}
                              >
                                <Flag /> Report
                              </OfferMenuItem>
                            )}
                          </OfferMenuDropdown>
                        )}
                      </OfferMenu>
                    )}
                  </OfferHeaderTopRow>
                  {offer.status === 'accepted' && (
                    <OfferStatusBadge status={offer.status}>
                      Selected
                    </OfferStatusBadge>
                  )}
                </OfferHeaderSide>
              </OfferHeader>

              <OfferProposal>{offer.proposal}</OfferProposal>

              {offer.previous_similar_work && (
                <OfferSection>
                  <OfferSectionTitle>Similar Work</OfferSectionTitle>
                  <OfferSectionContent>
                    {offer.previous_similar_work}
                  </OfferSectionContent>
                </OfferSection>
              )}

              {offer.portfolio_links && offer.portfolio_links.length > 0 && (
                <PortfolioLinks>
                  {offer.portfolio_links
                    .filter(link => link && isValidURL(link))
                    .slice(0, 3)
                    .map((link, index) => {
                      const normalized = normalizeURL(link);
                      const domain = getDomainName(link);
                      return (
                        <PortfolioLink
                          key={index}
                          href={normalized}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <ExternalLink />
                          {domain}
                        </PortfolioLink>
                      );
                    })}
                </PortfolioLinks>
              )}

              <OfferMeta>
                <OfferMetaItem>
                  <div className="label">Timeline</div>
                  <div className="value">{offer.duration || "N/A"}</div>
                </OfferMetaItem>
                {offer.years_experience && (
                  <ExperienceTag>
                    {offer.years_experience} yrs exp
                  </ExperienceTag>
                )}
              </OfferMeta>
              {(offerQuestions.length > 0 || canPostOfferChat || isOfferQuestionsLoading) && (
                <OfferChat>
                  <OfferChatHeader>
                    <MessageCircle size={16} />
                    Offer Discussion
                    <OfferChatCount>({offerQuestions.length})</OfferChatCount>
                    {(canPostOfferChat || offerQuestions.length > 0) && (
                      <OfferChatToggle
                        onClick={() =>
                          setOfferChatExpandedById((p) => ({
                            ...p,
                            [offer.id]: !isOfferChatExpanded,
                          }))
                        }
                      >
                        {offerQuestions.length === 0 && canPostOfferChat
                          ? "Start discussion"
                          : isOfferChatExpanded
                          ? "Hide"
                          : "View"}
                      </OfferChatToggle>
                    )}
                  </OfferChatHeader>
                  {isOfferQuestionsLoading ? (
                    <OfferChatEmpty>Loading messages…</OfferChatEmpty>
                  ) : offerQuestions.length && isOfferChatExpanded ? (
                    <OfferChatMessages>
                      {offerQuestions.flatMap((q) => {
                        const base = [{
                          id: `q-${q.id}`,
                          user_id: q.user_id,
                          user_name: q.user_name,
                          avatar: q.avatar,
                          email_verified: q.email_verified,
                          text: q.question_text || q.text,
                          created_at: q.created_at,
                        }];
                        const replies = (q.replies || []).map((reply) => ({
                          id: `r-${reply.id}`,
                          user_id: reply.user_id,
                          user_name: reply.user_name,
                          avatar: reply.avatar,
                          email_verified: reply.email_verified,
                          text: reply.reply_text || reply.text,
                          created_at: reply.created_at,
                        }));
                        return [...base, ...replies];
                      }).sort((a, b) => parseDate(a.created_at) - parseDate(b.created_at)).map((m) => (
                        <OfferChatMessage key={m.id}>
                          <Avatar
                            src={m.avatar}
                            name={m.user_name}
                            userId={m.user_id}
                            size="28px"
                          />
                          <OfferChatBubble>
                            <OfferChatMeta>
                              {m.user_id ? (
                                <Link to={`/users/${m.user_id}`}>
                                  {(m.user_name || "Anonymous User").toLowerCase().replace(/\b\w/g, l => l.toUpperCase())}
                                </Link>
                              ) : (
                                <span>{(m.user_name || "Anonymous User").toLowerCase().replace(/\b\w/g, l => l.toUpperCase())}</span>
                              )}
                              <span>·</span>
                              <span>
                                {formatDistanceToNow(parseDate(m.created_at), {
                                  addSuffix: true,
                                })}
                              </span>
                            </OfferChatMeta>
                            <OfferChatText>{m.text}</OfferChatText>
                          </OfferChatBubble>
                        </OfferChatMessage>
                      ))}
                    </OfferChatMessages>
                  ) : null}

                  {canPostOfferChat && isOfferChatExpanded && offer.status !== 'accepted' ? (
                    <ReplyForm style={{ marginTop: "6px" }}>
                      <ReplyTextarea
                        value={offerQuestionText}
                        onChange={(e) =>
                          setOfferQuestionTextById((p) => ({
                            ...p,
                            [offer.id]: e.target.value,
                          }))
                        }
                        placeholder="Message about this offer..."
                      />
                    <ReplyActions>
                      <ReplyButton
                        className="primary"
                        onClick={() => handleOfferQuestionSubmit(offer.id)}
                        disabled={!offerQuestionText.trim() || isOfferSubmitting}
                      >
                          {isOfferSubmitting ? "Posting..." : "Post Message"}
                        </ReplyButton>
                      </ReplyActions>
                    </ReplyForm>
                  ) : null}
                  {offer.status === 'accepted' && (
                    <OfferChatEmpty>
                      Offer accepted — continue in{" "}
                      <OfferChatLink to="/chat">Project Chat</OfferChatLink>.
                    </OfferChatEmpty>
                  )}
                </OfferChat>
              )}
            </OfferCard>
          );
          })
          ) : (
            <Empty>No offers yet — be the first to pitch!</Empty>
          )}
        </>
      ) : (
        <div>
          {questions?.length ? (
            questions.map((q, i) => (
              <QuestionCard
                key={q.id}
                style={{ animationDelay: `${i * 80}ms` }}
              >
                <QuestionHeader>
                  <Avatar
                    src={q.avatar}
                    name={q.user_name}
                    userId={q.user_id}
                    size="40px"
                  />
                  <QuestionUserInfo>
                    <QuestionUserName>
                      {q.user_id ? (
                        <Link to={`/users/${q.user_id}`}>
                          {(q.user_name || "Anonymous User").toLowerCase().replace(/\b\w/g, l => l.toUpperCase())}
                        </Link>
                      ) : (
                        <span>
                          {(q.user_name || "Anonymous User").toLowerCase().replace(/\b\w/g, l => l.toUpperCase())}
                        </span>
                      )}
                      <UserRating
                        userId={q.user_id}
                        averageRating={q.average_rating}
                        totalReviews={q.total_reviews}
                      />
                    </QuestionUserName>
                    <QuestionTimestamp>
                      {formatDistanceToNow(parseDate(q.created_at), {
                        addSuffix: true,
                      })}
                      {q.user_id === userState?.user?.user_id && editingQuestion !== q.id && (
                        <span
                          onClick={() => handleEditQuestion(q)}
                          style={{
                            marginLeft: '8px',
                            color: '#6b7280',
                            cursor: 'pointer',
                            fontSize: '0.8rem'
                          }}
                          onMouseEnter={(e) => e.target.style.color = '#3b82f6'}
                          onMouseLeave={(e) => e.target.style.color = '#6b7280'}
                        >
                          · Edit
                        </span>
                      )}
                    </QuestionTimestamp>
                  </QuestionUserInfo>
                  <QuestionIcon>
                    <MessageCircle size={18} />
                  </QuestionIcon>
                </QuestionHeader>

                {editingQuestion === q.id ? (
                  <ReplyForm style={{ marginBottom: '16px' }}>
                    <ReplyTextarea
                      value={editQuestionText}
                      onChange={(e) => setEditQuestionText(e.target.value)}
                      placeholder="Edit your question..."
                    />
                    <ReplyActions>
                      <ReplyButton
                        className="secondary"
                        onClick={handleCancelEdit}
                      >
                        Cancel
                      </ReplyButton>
                      <ReplyButton
                        className="primary"
                        onClick={() => handleEditSubmit(q.id)}
                        disabled={!editQuestionText.trim() || editSubmitting}
                      >
                        {editSubmitting ? "Saving..." : "Save"}
                      </ReplyButton>
                    </ReplyActions>
                  </ReplyForm>
                ) : (
                  <QuestionText>{q.question_text || q.text}</QuestionText>
                )}

                {/* Display replies if they exist */}
                {q.replies && q.replies.length > 0 && (
                  <RepliesContainer>
                    {q.replies.map((reply) => (
                      <ReplyItem key={reply.id}>
                        <ReplyHeader>
                          <Avatar
                            src={reply.avatar}
                            name={reply.user_name}
                            userId={reply.user_id}
                            size="24px"
                          />
                          <ReplyUserName onClick={() => { window.location.href = `/users/${reply.user_id}`; }}>
                            {(reply.user_name || "Anonymous User").toLowerCase().replace(/\b\w/g, l => l.toUpperCase())}
                          </ReplyUserName>
                          <ReplyTimestamp>
                            {formatDistanceToNow(parseDate(reply.created_at), {
                              addSuffix: true,
                            })}
                            {reply.user_id === userState?.user?.user_id && editingReply !== reply.id && (
                              <span
                                onClick={() => handleEditReply(reply)}
                                style={{
                                  marginLeft: '8px',
                                  color: '#6b7280',
                                  cursor: 'pointer',
                                }}
                                onMouseEnter={(e) => e.target.style.color = '#3b82f6'}
                                onMouseLeave={(e) => e.target.style.color = '#6b7280'}
                              >
                                · Edit
                              </span>
                            )}
                          </ReplyTimestamp>
                        </ReplyHeader>
                        {editingReply === reply.id ? (
                          <ReplyForm style={{ marginTop: '8px', marginBottom: '16px', marginLeft: '0' }}>
                            <ReplyTextarea
                              value={editReplyText}
                              onChange={(e) => setEditReplyText(e.target.value)}
                              placeholder="Edit your reply..."
                            />
                            <ReplyActions>
                              <ReplyButton
                                className="secondary"
                                onClick={handleCancelEditReply}
                              >
                                Cancel
                              </ReplyButton>
                              <ReplyButton
                                className="primary"
                                onClick={() => handleEditReplySubmit(reply.id)}
                                disabled={!editReplyText.trim() || editReplySubmitting}
                              >
                                {editReplySubmitting ? "Saving..." : "Save"}
                              </ReplyButton>
                            </ReplyActions>
                          </ReplyForm>
                        ) : (
                          <ReplyText>{reply.reply_text || reply.text}</ReplyText>
                        )}
                      </ReplyItem>
                    ))}
                  </RepliesContainer>
                )}
                
                {/* Reply functionality - show for task poster or question asker */}
                {(selectedTask?.poster?.id === userState?.user?.user_id || q.user_id === userState?.user?.user_id) ? (
                  replyingTo === q.id ? (
                    <ReplyForm>
                      <ReplyTextarea
                        value={replyText}
                        onChange={(e) => setReplyText(e.target.value)}
                        placeholder="Write your reply..."
                      />
                      <ReplyActions>
                        <ReplyButton
                          className="secondary"
                          onClick={() => {
                            setReplyingTo(null);
                            setReplyText("");
                          }}
                        >
                          Cancel
                        </ReplyButton>
                        <ReplyButton
                          className="primary"
                          onClick={() => handleReplySubmit(q.id)}
                          disabled={!replyText.trim() || replySubmitting}
                        >
                          {replySubmitting ? "Submitting..." : "Submit Reply"}
                        </ReplyButton>
                      </ReplyActions>
                    </ReplyForm>
                  ) : (
                    <div style={{ marginTop: "12px" }}>
                      <ReplyButton
                        className="secondary"
                        onClick={() => setReplyingTo(q.id)}
                      >
                        Reply to Question
                      </ReplyButton>
                    </div>
                  )
                ) : null}
              </QuestionCard>
            ))
          ) : (
            <Empty>No questions yet — be the first to ask!</Empty>
          )}
        </div>
      )}
    </Section>
  );
};

const mapStateToProps = (state) => ({ userState: state });
export default connect(mapStateToProps)(BrowseTasksPage);
